
<script setup>

// Zähle selektierte Leads für den Button-Text
const selectedLeadsCount = computed(() => {
  const list = leadGenerationStore.leadList?.data || [];
  return list.filter(lead => lead.selection && (lead.selection.google || lead.selection.businessPartner)).length;
});

// Gibt true zurück, wenn dateUnassign in der Zukunft liegt (Countdown läuft)
function parseDateUnassign(dateUnassign) {
  if (!dateUnassign) return null;

  if (dateUnassign instanceof Date) {
    return isNaN(dateUnassign.getTime()) ? null : dateUnassign;
  }

  if (typeof dateUnassign === 'number') {
    const byNumber = new Date(dateUnassign);
    return isNaN(byNumber.getTime()) ? null : byNumber;
  }

  if (typeof dateUnassign !== 'string') return null;

  const raw = dateUnassign.trim();
  if (!raw) return null;

  const direct = new Date(raw);
  if (!isNaN(direct.getTime())) return direct;

  const withT = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
  const normalized = withT.includes('T') && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(withT) ? `${withT}:00` : withT;
  const byIsoLike = new Date(normalized);
  if (!isNaN(byIsoLike.getTime())) return byIsoLike;

  // Fallback für deutsches Datumsformat: dd.mm.yyyy HH:mm(:ss)
  const deMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (deMatch) {
    const [, dd, mm, yyyy, hh = '00', min = '00', sec = '00'] = deMatch;
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(sec));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function isDateUnassignActive(dateUnassign) {
  const parsed = parseDateUnassign(dateUnassign);
  return !!parsed && parsed.getTime() > Date.now();
}

function isOwnLead(lead) {
  if (!lead.match?.status?.salesAgent?.idUser) return false;
  return lead.match.status.salesAgent.idUser === userStore.id;
}
  import { ref, onMounted, onUnmounted, computed, watch } from 'vue'

  // Liefert das aktuell ausgewählte Lead-Objekt aus der Liste
  const getSelectedLead = computed(() => {
    return leadGenerationStore.leadLists.find(l => l.signature === selectedQuerySignature.value) || null;
  });

  // Für Hover/Touch-Highlighting der Matching-Zeile
  const hoveredRow = ref(null)
  // Reset loading state on unmount to prevent stuck spinner
  onUnmounted(() => {
    userStore.currentSelection.leadGeneration.isLoading = false;
  });
  // Kapazitätslimit für Eigenrecherche Neukunden (aus DB via userStore, Fallback 100)
  const MAX_EIGENRECHERCHE = computed(() => userStore.maxReservations ?? 100);

  // Initialwert aus Backend/store
  const initialEigenrechercheCount = ref(0);
  // Dynamisch: Anzahl aktuell ausgewählter Leads
  const selectedEigenrechercheCount = computed(() => {
    const list = leadGenerationStore.leadList?.data || [];
    return list.filter(lead => lead.selection && (lead.selection.google || lead.selection.businessPartner)).length;
  });
  // Gesamt-Kapazität
  const eigenrechercheCount = computed(() => initialEigenrechercheCount.value + selectedEigenrechercheCount.value);
  const isAtCapacity = computed(() => eigenrechercheCount.value >= MAX_EIGENRECHERCHE.value);
  const isOverCapacity = computed(() => eigenrechercheCount.value > MAX_EIGENRECHERCHE.value);
  // Anzeige-String für Kapazität
  const eigenrechercheDisplay = computed(() => `${initialEigenrechercheCount.value} (${selectedEigenrechercheCount.value}) / ${MAX_EIGENRECHERCHE.value}`);
  
  import router from '../../router/routes';
  import { useRoute } from 'vue-router'
  import { fetchFromBackend } from '@/stores/helpers/backend.mjs'
  const route = useRoute()


  import Reminder from '@/components/Reminder.vue'
  import userAvatar from '@/components/userAvatar.vue'
  import CountdownBadge from '@/components/CountdownBadge.vue'
  import iconChart from '@/assets/imgs/icons/chart-line-solid-full.svg'
  import iconInfo from '@/assets/imgs/icons/circle-info-solid.svg'
  import iconLocation from '@/assets/imgs/icons/location-dot-solid.svg'
  import iconPhone from '@/assets/imgs/icons/phone-solid.svg'
  import iconLink from '@/assets/imgs/icons/link-solid.svg'
  import iconBuilding from '@/assets/imgs/icons/building-circle-check-solid.svg'
  import iconLock from '@/assets/imgs/icons/lock-solid.svg'
  import iconCheck from '@/assets/imgs/icons/circle-check-solid.svg'
  import iconQuestion from '@/assets/imgs/icons/circle-question-solid.svg'

  // Hilfsfunktion für die Subline-Auswahl im userAvatar
  function isInBearbeitungStatus(status) {
    return Number(status?.statusDate) === 1 || Number(status?.statusFollowUp) === 1;
  }

  function isClosedStatus(status) {
    return Number(status?.statusClosed) === 1;
  }

  function isLockedAssignedStatus(status) {
    return isInBearbeitungStatus(status) || isClosedStatus(status);
  }

  function getAssignedStatusLabel(status) {
    if (isClosedStatus(status)) return 'Abgeschlossen';
    return isInBearbeitungStatus(status) ? 'in Bearbeitung' : 'Zugeordnet';
  }

  function getStatusSubline(status) {
    if (!status) return '';
    if (status.salesAgent && status.salesAgent.firstName !== 'Dummy') {
      return getAssignedStatusLabel(status);
    }
    // Alle anderen Status werden im sm-wrapper angezeigt, Subline bleibt leer
    return '';
  }

  // Gibt true zurück, wenn der GP aktive Verträge/Produkte hat (irgendein Segment ist kein Platzhalter)
  function hasActiveContracts(status) {
    if (!status) return false;
    const contracts = status.contracts;
    if (!contracts) return false;

    // If contracts is an array, any non-empty entry indicates active contracts
    if (Array.isArray(contracts)) {
      return contracts.length > 0;
    }

    // If contracts is an object, check for any truthy property
    if (typeof contracts === 'object') {
      return Object.values(contracts).some(v => !!v);
    }

    // If contracts is a string, normalize and check for known product tokens
    if (typeof contracts === 'string') {
      const s = contracts.trim();
      if (!s) return false;
      if (s === '--- -- -- --') return false;
      // Check for common product codes (case-insensitive)
      if (/\b(GS|TB|OE|OMP)\b/i.test(s)) return true;
      // Fallback: any uppercase letter or digit indicates non-placeholder
      return /[A-Z0-9]/.test(s);
    }

    return false;
  }

  import { useUserStore } from '@/stores/user'
  const userStore = useUserStore();

  import { useLeadGenerationStore } from '@/stores/leadGeneration'
  const leadGenerationStore = useLeadGenerationStore();
  import { useBusinessPartnersStore } from '@/stores/businessPartners';
  const businessPartnersStore = useBusinessPartnersStore();
  import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount';
  const businessPartnersCountStore = useBusinessPartnersCountStore();

  import Bar from '@/components/Bar_new.vue';
  import PreviewHeader from '@/components/PreviewHeader.vue';


  const hoveredLead = ref(null)
  const conversionFeedback = ref(null)
  const showConversionFeedback = ref(false)
  const convertedQuerySignature = ref('')
  const openedConversionEntryKeys = ref([])
  const CONVERSION_POPUP_STORAGE_KEY = 'leadGeneration:conversionPopupState'
  const CONVERSION_POPUP_TTL_MS = 2 * 60 * 60 * 1000

  function getConversionEntryKey(entry) {
    if (!entry) return '';
    return String(
      entry.gpNo
      || entry.meta?.gpNo
      || entry.gp?.no
      || entry.id
      || normalizeLookupName(entry.name)
      || ''
    ).trim();
  }

  function persistConversionPopupState() {
    try {
      if (!showConversionFeedback.value || !conversionFeedback.value) {
        sessionStorage.removeItem(CONVERSION_POPUP_STORAGE_KEY);
        return;
      }
      const payload = {
        savedAt: Date.now(),
        convertedQuerySignature: convertedQuerySignature.value || userStore.currentSelection?.leadGeneration?.signature || '',
        conversionFeedback: conversionFeedback.value,
        openedEntryKeys: openedConversionEntryKeys.value,
      };
      sessionStorage.setItem(CONVERSION_POPUP_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('persistConversionPopupState failed', e);
    }
  }

  function clearPersistedConversionPopupState() {
    try {
      sessionStorage.removeItem(CONVERSION_POPUP_STORAGE_KEY);
    } catch (e) {
      console.warn('clearPersistedConversionPopupState failed', e);
    }
  }

  function restoreConversionPopupState() {
    try {
      const raw = sessionStorage.getItem(CONVERSION_POPUP_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        clearPersistedConversionPopupState();
        return;
      }
      if (!parsed.savedAt || (Date.now() - Number(parsed.savedAt)) > CONVERSION_POPUP_TTL_MS) {
        clearPersistedConversionPopupState();
        return;
      }
      if (!parsed.conversionFeedback) {
        clearPersistedConversionPopupState();
        return;
      }

      conversionFeedback.value = parsed.conversionFeedback;
      convertedQuerySignature.value = String(parsed.convertedQuerySignature || '');
      openedConversionEntryKeys.value = Array.isArray(parsed.openedEntryKeys)
        ? parsed.openedEntryKeys.map((v) => String(v)).filter(Boolean)
        : [];
      showConversionFeedback.value = true;
    } catch (e) {
      console.warn('restoreConversionPopupState failed', e);
      clearPersistedConversionPopupState();
    }
  }

  function shouldResumeConversionPopupFromRoute() {
    return String(route.query.resumeConversionPopup || '').toLowerCase() === 'true';
  }

  function markConversionEntryAsOpened(entry) {
    const key = getConversionEntryKey(entry);
    if (!key) return;
    if (!openedConversionEntryKeys.value.includes(key)) {
      openedConversionEntryKeys.value.push(key);
      persistConversionPopupState();
    }
  }

  function isConversionEntryOpened(entry) {
    const key = getConversionEntryKey(entry);
    return !!key && openedConversionEntryKeys.value.includes(key);
  }

  function normalizeLeadName(value) {
    return String(value || '').trim().toLowerCase();
  }

  function normalizeLookupName(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  const selectedLeadGpNoByName = computed(() => {
    const map = new Map();
    const list = leadGenerationStore.leadList?.data || [];
    for (const lead of list) {
      const isSelected = !!(lead?.selection?.google || lead?.selection?.businessPartner);
      if (!isSelected) continue;
      const gpNo = lead?.match?.meta?.gpNo || lead?.match?.gpNo || null;
      if (!gpNo) continue;
      const leadNameKey = normalizeLeadName(lead?.name);
      if (leadNameKey && !map.has(leadNameKey)) map.set(leadNameKey, gpNo);
      const matchNameKey = normalizeLeadName(lead?.match?.name);
      if (matchNameKey && !map.has(matchNameKey)) map.set(matchNameKey, gpNo);
    }
    return map;
  });

  const createdBusinessPartners = computed(() => {
    const raw = Array.isArray(conversionFeedback.value?.businessPartnersCreated)
      ? conversionFeedback.value.businessPartnersCreated
      : [];
    return raw.map((entry) => {
      const mappedGpNo = selectedLeadGpNoByName.value.get(normalizeLeadName(entry?.name)) || null;
      return {
        ...entry,
        gpNo: entry?.gpNo || entry?.meta?.gpNo || mappedGpNo || null
      };
    });
  });

  const conversionErrors = computed(() => {
    return Array.isArray(conversionFeedback.value?.errors)
      ? conversionFeedback.value.errors
      : [];
  });

  const conversionNextActionLabel = computed(() => {
    const convertedSignature = convertedQuerySignature.value || userStore.currentSelection.leadGeneration.signature;
    const hasAnotherSearch = leadGenerationStore.leadLists.some((list) => list.signature !== convertedSignature);
    return hasAnotherSearch ? 'Nächste Suche öffnen' : 'Zum Dashboard';
  });


  // Fortschritt für Ladebalken (immer sichtbar, schrittweise)
  const loadingBarWidth = computed(() => {
    const lead = getSelectedLead.value;
    if (!lead || !lead.data || !Array.isArray(lead.data) || lead.data.length === 0) return 0;
    const processed = getProcessedCount(lead.data);
    return Math.round((processed / lead.data.length) * 100);
  });

  // Potenzial: Alle übernehmbaren Treffer aus den drei Containern
  const getPotentialCount = computed(() => {
    const lead = getSelectedLead.value;
    if (!lead || !lead.data || !Array.isArray(lead.data)) return 0;
    return lead.data.filter(l => l.processed === true && (
      l.convertToBusinessPartner === 'allow-lead' ||
      l.convertToBusinessPartner === 'allow-both' ||
      l.convertToBusinessPartner === 'allow-businessPartner')
    ).length;
  });

  const selectedQuerySignature = ref('');
    onMounted(() => {
      if (leadGenerationStore.leadLists.length > 0) {
        selectedQuerySignature.value = leadGenerationStore.leadLists[0].signature;
      }
    });

  watch(
    () => route.query.resumeConversionPopup,
    async () => {
      if (!shouldResumeConversionPopupFromRoute()) return;
      restoreConversionPopupState();
      const sanitizedQuery = { ...route.query };
      delete sanitizedQuery.resumeConversionPopup;
      await router.replace({ query: sanitizedQuery });
    },
    { immediate: true }
  );

// Keep selectedQuerySignature in sync with route query.signature (e.g. when navigating from preview)
watch(() => route.query.signature, (newSig) => {
  if (!newSig) return;
  // Ensure the signature stored locally matches the route and update store selection
  if (selectedQuerySignature.value !== newSig) {
    handleSelectLeadList(newSig);
  }
});

    // Hilfsfunktion: Finde den eigenen Kollegen-Eintrag
  const myColleague = computed(() => {
    if (!Array.isArray(userStore.colleagues)) return null;
    return userStore.colleagues.find(c => c.id === userStore.id) || null;
  });

  // Mindestens eine Checkbox ausgewählt?
  const hasCheckedLead = computed(() => {
    const list = leadGenerationStore.leadList?.data || [];
    return list.some(lead => lead.selection && (lead.selection.google || lead.selection.businessPartner));
  });

  // Button-Disable-Logik und Hinweistext
  const canConvert = computed(() => {
    if (!myColleague.value) return false;
    // Allow if user is sales agent or has salesTeamManager role
    const isSalesAgent = !!myColleague.value.isSalesAgent;
    const isSalesTeamManager = Array.isArray(myColleague.value.roles) && myColleague.value.roles.some(r => r.idRole === 7);
    return (isSalesAgent || isSalesTeamManager) && hasCheckedLead.value && !isOverCapacity.value;
  });
  const convertHint = computed(() => {
    if (isOverCapacity.value) {
      return 'Kapazitätslimit erreicht: Bitte bearbeiten Sie zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu erhalten.';
    }
    if (!myColleague.value || !myColleague.value.isSalesAgent) {
      return 'Recherche-Übernahme ist nur als Sales Agent und Area Manager möglich.';
    }
    if (!hasCheckedLead.value) {
      return 'Bitte mindestens einen Eintrag auswählen, um die Recherche zu übernehmen.';
    }
    return '';
  });

  onMounted(async () => {
    if(userStore.selectedMedia === 'desktop') {
      userStore.currentSelection.leadGeneration.layout = 'list'
    }
    setTimeout(() => {
      scrollToTarget();
    }, 100);
    // Eigenrecherche-Count abrufen, aber nur wenn nicht schon im Store
    const filter = businessPartnersCountStore.namedFilter('eigenrechercheUnbeendetCount');
    const targetSignature = filter?.signature;
    let lists = businessPartnersCountStore.businessPartnerLists.find(l =>
      (l.signature && l.signature === targetSignature) ||
      (l.filter && l.filter.signature && l.filter.signature === targetSignature)
    );
    if (!lists) {
      await businessPartnersCountStore.getByFilter(filter);
      lists = businessPartnersCountStore.businessPartnerLists.find(l =>
        (l.signature && l.signature === targetSignature) ||
        (l.filter && l.filter.signature && l.filter.signature === targetSignature)
      );
    }
    if (lists && typeof lists.count === 'number') {
      initialEigenrechercheCount.value = lists.count;
    }
  });

  // compares elements of the lead with its businessPartner match to highligh them
  function getLeadHoverId(lead) {
    return String(lead?.meta?.googlePlaces?.id ?? lead?.id ?? '');
  }

  function handleCompareLeadToBusinessPartners(lead) {
    hoveredLead.value = getLeadHoverId(lead);
  }

  function isLeadHovered(lead) {
    const id = getLeadHoverId(lead);
    return !!id && hoveredLead.value === id;
  }

  function clearLeadHover() {
    hoveredLead.value = null;
  }

  // toggle leadSelection
  function handleSelectLead(lead, selectionType) {
    console.log('lead selected', lead, selectionType);
    if(lead.convertToBusinessPartner==='block') return false
    if (selectionType === 'google') lead.selection.businessPartner = false
    if (selectionType === 'businessPartner') lead.selection.google = false
  }

  function isSelectionDisabledAtCapacity(lead, selectionType) {
    // Bei vollem Limit sollen bereits gewählte Checkboxen weiterhin abwählbar bleiben.
    if (!isAtCapacity.value) return false;
    return !(lead?.selection?.[selectionType]);
  }

  async function lookupGpNoForCreatedEntry(entry) {
    if (!entry?.id && !entry?.name) return null;
    try {
      // Scope-abhängige Filter analog zu businessPartners Store
      const scope = userStore.currentSelection.scope;
      let idSalesAgents = -1;
      let idSalesTeams = -1;
      if (scope === 'salesAgent') {
        idSalesAgents = userStore.currentSelection?.salesAgent?.id ?? -1;
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      } else if (scope === 'salesTeam') {
        idSalesTeams = userStore.currentSelection.salesTeam.id ?? -1;
      }

      const payload = {
        id: userStore.id,
        filter: {
          idCompany: userStore.currentSelection.campaign.company.id,
          idCampaign: userStore.currentSelection.campaign.id,
          idSalesAgents,
          idSalesTeams,
          search: { mode: 'id', string: String(entry.id || ''), wholeWord: 0 }
        }
      };
      let response = await fetchFromBackend('businessPartners/v1/getBusinessPartner', payload);

      let list = Array.isArray(response?.businessPartnerList) ? response.businessPartnerList
        : Array.isArray(response) ? response : [];

      // Fallback: Name-Suche, wenn per ID nichts gefunden wurde.
      if (!list.length && entry?.name) {
        payload.filter.search = { mode: 'name', string: entry.name, wholeWord: 1 };
        response = await fetchFromBackend('businessPartners/v1/getBusinessPartner', payload);
        list = Array.isArray(response?.businessPartnerList) ? response.businessPartnerList
          : Array.isArray(response) ? response : [];

        // Zweiter Name-Fallback: unscharf
        if (!list.length) {
          payload.filter.search.wholeWord = 0;
          response = await fetchFromBackend('businessPartners/v1/getBusinessPartner', payload);
          list = Array.isArray(response?.businessPartnerList) ? response.businessPartnerList
            : Array.isArray(response) ? response : [];
        }
      }

      // Shape-Normalisierung: gp.no → gpNo, gp.name → name (analog zum Store)
      list = list.map((bp) => {
        if (bp?.gp?.no && !bp.gpNo) bp.gpNo = String(bp.gp.no);
        if (bp?.gp?.name && !bp.name) bp.name = bp.gp.name;
        return bp;
      });

      const entryLookupName = normalizeLookupName(entry.name || '');

      // Matching: zuerst per interner ID, dann exakter Name, dann normalisierte Schreibweise
      const match = list.find((bp) =>
        (entry.id && String(bp?.id) === String(entry.id))
      ) || list.find((bp) =>
        normalizeLeadName(bp?.name || '') === normalizeLeadName(entry.name)
      ) || list.find((bp) =>
        normalizeLookupName(bp?.name || '') === entryLookupName
      );
      const gpNo = match?.gpNo || match?.gp?.no || null;
      return gpNo ? String(gpNo) : null;
    } catch {
      return null;
    }
  }

  async function handleConvertLeads() {
    const activeSignature = userStore.currentSelection.leadGeneration.signature;
    userStore.currentSelection.leadGeneration.isLoading = true;
    const convertLeadsResult = await leadGenerationStore.convertLeads();
    userStore.currentSelection.leadGeneration.isLoading = false;

    if (convertLeadsResult) {
      convertedQuerySignature.value = activeSignature || '';
      // Versuche für neu erstellte Einträge ohne GPNo die GP-Nummer nachzuladen
      if (Array.isArray(convertLeadsResult.businessPartnersCreated)) {
        const enriched = await Promise.all(
          convertLeadsResult.businessPartnersCreated.map(async (entry) => {
            if (entry.gpNo || entry.meta?.gpNo) return entry;
            const lookedUpGpNo = await lookupGpNoForCreatedEntry(entry);
            return lookedUpGpNo ? { ...entry, gpNo: lookedUpGpNo } : entry;
          })
        );
        convertLeadsResult.businessPartnersCreated = enriched;
      }
      conversionFeedback.value = convertLeadsResult;
      showConversionFeedback.value = true;
      openedConversionEntryKeys.value = [];
      persistConversionPopupState();
      await userStore.refreshFilterOptions()
    }
  }

  async function handleAbortConversion() {
    const activeSignature = userStore.currentSelection.leadGeneration.signature;
    leadGenerationStore.removeQuery(activeSignature);

    const nextLeadList = leadGenerationStore.leadLists[0] || null;
    if (nextLeadList?.signature) {
      await handleSelectLeadList(nextLeadList.signature);
      return;
    }

    router.push({ name: 'Dashboard' });
  }

  async function closeConversionFeedback() {
    const convertedSignature = convertedQuerySignature.value || userStore.currentSelection.leadGeneration.signature;
    clearPersistedConversionPopupState();
    if (convertedSignature) {
      leadGenerationStore.removeQuery(convertedSignature);
    }

    const nextLeadList = leadGenerationStore.leadLists[0] || null;

    if (nextLeadList?.signature) {
      await handleSelectLeadList(nextLeadList.signature);
      showConversionFeedback.value = false;
      convertedQuerySignature.value = '';
      conversionFeedback.value = null;
      openedConversionEntryKeys.value = [];
      return;
    }

    showConversionFeedback.value = false;
    convertedQuerySignature.value = '';
    conversionFeedback.value = null;
    openedConversionEntryKeys.value = [];
    router.push({ name: 'Dashboard' });
  }

  function resolveConversionBusinessPartnerGpNo(entry) {
    if (!entry) return null;
    return entry.gpNo
      || entry.gp?.no
      || entry.gp_no
      || entry.gPNo
      || entry.GP
      || entry.meta?.gpNo
      || entry.match?.meta?.gpNo
      || entry.businessPartner?.gpNo
      || entry.businessPartner?.meta?.gpNo
      || entry.businessPartner?.match?.meta?.gpNo
      || businessPartnersStore.businessPartnerList?.data?.find((bp) => String(bp?.id) === String(entry.id))?.gpNo
      || businessPartnersStore.businessPartners?.find((bp) => String(bp?.id) === String(entry.id))?.gpNo
      || null;
  }

  function createConversionBusinessPartnerRoute(gpNo) {
    const activeSignature = convertedQuerySignature.value || userStore.currentSelection?.leadGeneration?.signature || '';
    return {
      name: 'BusinessPartner',
      query: {
        searchMode: 'GPNo',
        search: String(gpNo),
        fromResearchList: 'true',
        resumeConversionPopup: 'true',
        ...(activeSignature ? { signature: activeSignature } : {})
      }
    };
  }

  function getConversionBusinessPartnerLink(entry) {
    if (!entry) return null;
    const resolvedGpNo = resolveConversionBusinessPartnerGpNo(entry);
    if (resolvedGpNo) return createConversionBusinessPartnerRoute(resolvedGpNo);
    // Kein GPNo vorhanden – kein direkter Link möglich (Google-Neukunde ohne Bestandsmatch)
    return null;
  }

  async function openBusinessPartnerFromConversion(entry) {
    if (!entry) return;

    let resolvedGpNo = resolveConversionBusinessPartnerGpNo(entry);
    if (!resolvedGpNo) {
      resolvedGpNo = await lookupGpNoForCreatedEntry(entry);
      if (resolvedGpNo) {
        entry.gpNo = resolvedGpNo;
      }
    }

    if (!resolvedGpNo) {
      clearPersistedConversionPopupState();
      openConversionSearchList();
      return;
    }
    markConversionEntryAsOpened(entry);
    persistConversionPopupState();
    router.push(createConversionBusinessPartnerRoute(resolvedGpNo));
  }

  function openConversionSearchList() {
    const allLists = userStore.filterOption?.businessPartnerLists || [];
    const opportunityName = String(conversionFeedback.value?.opportunity?.name || '').trim();
    const opportunityId = conversionFeedback.value?.opportunity?.id;
    const leadSignature = String(userStore.currentSelection?.leadGeneration?.signature || '');
    const signatureSearchName = decodeURIComponent(leadSignature.replace('googlePlacesSearch:', '')).trim();

    // Filter-UI expects IDs in the same shape as filter options (usually "list:<id>").
    let listId = null;

    if (opportunityId !== undefined && opportunityId !== null) {
      const byId = allLists.find((l) => l?.isList && String(l.id) === `list:${opportunityId}`);
      if (byId?.id) listId = byId.id;
    }

    if (!listId && opportunityName) {
      const normalized = opportunityName.toLowerCase();
      const byExactName = allLists.find((l) => l?.isList && String(l.name || '').trim().toLowerCase() === normalized);
      if (byExactName?.id) listId = byExactName.id;
    }

    if (!listId && opportunityName) {
      const normalized = opportunityName.toLowerCase();
      const byContains = allLists.find((l) => l?.isList && String(l.name || '').toLowerCase().includes(normalized));
      if (byContains?.id) listId = byContains.id;
    }

    if (!listId && signatureSearchName) {
      const normalizedSignatureName = signatureSearchName.toLowerCase();
      const bySignatureName = allLists.find(
        (l) => l?.isList && String(l.name || '').trim().toLowerCase() === normalizedSignatureName
      );
      if (bySignatureName?.id) listId = bySignatureName.id;
    }

    if (!listId) {
      const fallbackEigenrechercheList = allLists.find(
        (l) => l?.isList && String(l.name || '').toLowerCase().includes('eigenrecherche')
      );
      listId = fallbackEigenrechercheList?.id || -1;
    }

    userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList = listId;
    userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerStatus = null;
    userStore.currentSelection.businessPartnerList.source = 'filter';

    const filterSignature = userStore.currentFilter?.filter?.signature;

    (async () => {
      try {
        await router.push({
          name: 'BusinessPartnerList',
          query: {
            source: 'filter',
            ...(filterSignature ? { filter: filterSignature } : {}),
            fromResearchList: 'true'
          }
        });
      } finally {
        // Entferne die Eigenrecherche-Liste NACH der Navigation, damit die Route die Suche noch finden kann.
        try {
          if (leadSignature) {
            leadGenerationStore.removeQuery(leadSignature);
          }
        } catch (e) {
          console.warn('openConversionSearchList: failed to remove lead query after navigation', e);
        }
      }
    })();
  }
  

  // toggle between list and grid layout
  function handleToggleLayout() {
    if (userStore.currentSelection.leadGeneration.layout === 'list') {
      userStore.currentSelection.leadGeneration.layout = 'grid'
    } else {
      userStore.currentSelection.leadGeneration.layout = 'list'
    }
  }


  const dropdownVisible = ref(false);
  const toggleDropdown = () => {
      dropdownVisible.value = !dropdownVisible.value;
  };

  // Neue Funktion: Lade Liste neu, wenn Query gewechselt wird
  async function handleSelectLeadList(signature) {
    if (!signature) return;
    selectedQuerySignature.value = signature;
    // Setze die aktuelle Query im Store
    userStore.currentSelection.leadGeneration.signature = signature;

    const nextLeadList = leadGenerationStore.leadLists.find((list) => list.signature === signature) || null;
    if (!nextLeadList) {
      router.push({ name: 'Dashboard' });
    }
  }

  function formatSignature(signature) {
        if (!signature) return '';
        // Entferne den Präfix ":googlePlacesSearch:"
        const withoutPrefix = signature.replace('googlePlacesSearch:', '');
        // Dekodiere URL-kodierte Zeichen (z. B. %20 -> Leerzeichen)
        return decodeURIComponent(withoutPrefix);
  }

  function getProcessedCount(data) {
      if (!data || !Array.isArray(data)) return 0;
      return data.filter(item => item.processed).length;
  }

  function getLowConfidenceAndFarDistanceCount(data) {
      if (!data || !Array.isArray(data)) return 0;
      return data.filter(item => item.match?.confidence < 0.2 && item.match?.distance > 3).length;
  }

  function isCurrentLead(leadSignature) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlSignature = decodeURIComponent(urlParams.get('signature') || '');
      const decodedLeadSignature = decodeURIComponent(leadSignature);
  
      console.log('urlSignature', urlSignature);
      console.log('decodedLeadSignature', decodedLeadSignature);
      console.log('isCurrentLead', decodedLeadSignature === urlSignature);
  
      return decodedLeadSignature === urlSignature;
  }

  const filteredLeadLists = computed(() => {
    return leadGenerationStore.leadLists.filter(lead => isCurrentLead(lead.signature));
  });

    function formatMonthYear(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString; // Falls das Datum ungültig ist, gib den Originalwert zurück
      return new Intl.DateTimeFormat('de-DE', { month: 'short', year: '2-digit' }).format(date);
    }

    function renderStars(rating) {
        const maxStars = 5;
        const stars = [];
    
        for (let i = 1; i <= maxStars; i++) {
            stars.push(i <= rating ? 'yellow' : 'black');
        }
    
        return stars;
    }

    function getBusinessPartnerLink(businessPartner, index) {
      const namedFilter = route.query.namedFilter;
      let extraParam = {};

      if (namedFilter === 'Today:Wiedervorlagen') {
        extraParam = { fromTodayWiedervorlagenList: 'true' };
      } else if (namedFilter === 'Today:VorOrtTermine') {
        extraParam = { fromTodayVorOrtTermineList: 'true' };
      } else {
        extraParam = { fromResearchList: 'true' };
      }

      return {
        name: 'BusinessPartner',
        query: {
          searchMode: 'GPNo',
          search: businessPartner.gpNo,
          position: String(index ?? ''),
          signature: route.query.signature,
          ...extraParam
        }
      };
    }

    function scrollToTarget() {
      const businessPartnerGpNo = route.query.businessPartnerGpNo;
      const position = route.query.position;
    
      let target = null;
      if (businessPartnerGpNo) {
        target = document.getElementById(businessPartnerGpNo);
      }
      if (!target && position) {
        target = document.getElementById(position);
      }
    
      if (!target) {
        console.warn('Target not found');
        return;
      }
    
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('highlight');
      console.log('Scrolling to:', target.id);
    }
import InlineSpinner from '@/components/InlineSpinner.vue';

// Wählt alle Google-Leads ohne Matching-GP aus
function selectAllGoogleLeadsWithoutMatch() {
  const leads = leadGenerationStore.leadList?.data || [];
  leads.forEach(lead => {
    if (
      lead.processed === true &&
      lead.convertToBusinessPartner !== 'block' &&
      !lead.match
    ) {
      if (!lead.selection) lead.selection = {};
      lead.selection.google = true;
    }
  });
}

function deselectAllGoogleLeadsWithoutMatch() {
  const leads = leadGenerationStore.leadList?.data || [];
  leads.forEach(lead => {
    if (
      lead.processed === true &&
      lead.convertToBusinessPartner !== 'block' &&
      !lead.match
    ) {
      if (!lead.selection) lead.selection = {};
      lead.selection.google = false;
    }
  });
}

// Computed: Sind alle Google-Leads ohne Matching-GP ausgewählt?
  const areAllGoogleLeadsSelected = computed(() => {
  const leads = leadGenerationStore.leadList?.data?.filter(l => l.processed === true && l.convertToBusinessPartner !== 'block' && !l.match) || [];
  return leads.length > 0 && leads.every(lead => lead.selection && lead.selection.google);
  });

  // Handler: Alle Google-Leads ohne Matching-GP auswählen/abwählen
  function toggleAllGoogleLeads(event) {
  const checked = event.target.checked;
  const leads = leadGenerationStore.leadList?.data || [];
  leads.forEach(lead => {
  if (
    lead.processed === true &&
    lead.convertToBusinessPartner !== 'block' &&
    !lead.match
  ) {
    if (!lead.selection) lead.selection = {};
    lead.selection.google = checked;
  }
  });
}

// Dynamische Klasse für Kapazitätsfarbe
const capacityColorClass = computed(() => {
  const percent = (eigenrechercheCount.value / MAX_EIGENRECHERCHE.value) * 100;
  if (percent < 60) return 'capacity-green';
  if (percent < 100) return 'capacity-yellow';
  return 'capacity-red';
});

const processedLeads = computed(() => {
  return (leadGenerationStore.leadList?.data || []).filter(l => l.processed === true);
});

const qualityFilter = ref('all');

function getQualityBucket(lead) {
  if (lead?.matching?.qualityBucket) return lead.matching.qualityBucket;
  if (!lead?.match) return 'none';
  if (typeof lead?.matching?.weightedScore === 'number') {
    if (lead.matching.weightedScore >= 75) return 'high';
    if (lead.matching.weightedScore < 45) return 'low';
    return 'medium';
  }
  const confidence = Number(lead.match?.confidence || 0);
  const distance = Number(lead.match?.distance || 999);
  if (confidence >= 0.75 && distance <= 1.5) return 'high';
  if (confidence >= 0.5 && distance <= 5) return 'medium';
  return 'low';
}

function getQualityBucketLabel(bucket) {
  if (bucket === 'high') return 'Score';
  if (bucket === 'medium') return 'Score';
  if (bucket === 'low') return 'Unklar';
  return 'Kein Match';
}

function getTokenMatchRate(tokens) {
  if (!Array.isArray(tokens) || tokens.length === 0) return null;
  const matches = tokens.filter(t => !!t?.match).length;
  return matches / tokens.length;
}

function getMatchReasons(lead) {
  if (Array.isArray(lead?.matching?.reasons) && lead.matching.reasons.length > 0) {
    const details = [];
    if (lead?.match) {
      details.push(`Confidence ${(Number(lead.match?.confidence || 0) * 100).toFixed(0)}%`);
      details.push(`Distanz ${Number(lead.match?.distance || 0).toFixed(1)} km`);
    }
    return [...lead.matching.reasons, ...details].filter(Boolean).slice(0, 5);
  }

  const reasons = [];
  if (!lead?.match) {
    reasons.push('Kein Bestandsmatch gefunden');
    reasons.push('Google-Treffer kann direkt uebernommen werden');
    return reasons;
  }

  const confidence = Number(lead.match?.confidence || 0);
  const distance = Number(lead.match?.distance || 999);
  const nameRate = getTokenMatchRate(lead.matching?.name?.tokens);
  const addressRate = getTokenMatchRate(lead.matching?.address?.tokens);
  const phoneRate = getTokenMatchRate(lead.matching?.phoneNumbers?.tokens);
  const urlRate = getTokenMatchRate(lead.matching?.urls?.tokens);

  reasons.push(`Confidence ${(confidence * 100).toFixed(0)}%`);
  reasons.push(`Distanz ${distance.toFixed(1)} km`);

  if (nameRate !== null) reasons.push(`Name-Match ${(nameRate * 100).toFixed(0)}%`);
  if (addressRate !== null) reasons.push(`Adress-Match ${(addressRate * 100).toFixed(0)}%`);
  if (phoneRate !== null) reasons.push(`Telefon-Match ${(phoneRate * 100).toFixed(0)}%`);
  if (urlRate !== null) reasons.push(`URL-Match ${(urlRate * 100).toFixed(0)}%`);

  if (lead.match?.status?.contracts && lead.match.status.contracts !== '--- -- -- --') {
    reasons.push('Bestandskunde mit Produkten');
  }
  if (isDateUnassignActive(lead.match?.status?.dateUnassign)) {
    reasons.push('Temporär gesperrt bis Freigabezeitpunkt');
  }

  return reasons.slice(0, 4);
}

function getMatchExplanationTitle(lead) {
  return getMatchReasons(lead).join(' | ');
}

function getTrafficToneFromPercent(percent) {
  if (typeof percent !== 'number' || Number.isNaN(percent)) return 'warn';
  if (percent >= 75) return 'good';
  if (percent >= 45) return 'warn';
  return 'danger';
}

function parseMetricToPercent(metric) {
  const raw = String(metric || '').trim();
  if (!raw) return null;

  // Example: 41/100
  if (raw.includes('/')) {
    const [current, total] = raw.split('/').map(v => Number(String(v).replace(',', '.').replace(/[^0-9.]/g, '')));
    if (total > 0 && !Number.isNaN(current) && !Number.isNaN(total)) return (current / total) * 100;
  }

  // Example: 43% or 7.3 km
  const numeric = Number(raw.replace(',', '.').replace(/[^0-9.]/g, ''));
  if (Number.isNaN(numeric)) return null;
  return numeric;
}

function getReasonTone(reason, metric = '') {
  const txt = String(reason || '').toLowerCase();

  // Score + Confidence should always follow traffic-light logic.
  if (txt.includes('score') || txt.includes('confidence')) {
    const percent = parseMetricToPercent(metric);
    return getTrafficToneFromPercent(percent);
  }

  if (txt.includes('gesperrt') || txt.includes('schwach') || txt.includes('niedrige')) return 'danger';
  if (txt.includes('hohe distanz') || txt.includes('unklar')) return 'warn';
  if (txt.includes('stark') || txt.includes('sehr hohe') || txt.includes('kurze distanz')) return 'good';
  if (txt.includes('bestandskunde')) return 'indigo';
  return 'info';
}

function getReasonIconSrc(reason) {
  const txt = String(reason || '').toLowerCase();
  if (txt.includes('score')) return iconChart;
  if (txt.includes('confidence')) return iconInfo;
  if (txt.includes('distanz')) return iconLocation;
  if (txt.includes('adress')) return iconLocation;
  if (txt.includes('telefon')) return iconPhone;
  if (txt.includes('url')) return iconLink;
  if (txt.includes('bestandskunde')) return iconBuilding;
  if (txt.includes('gesperrt')) return iconLock;
  if (txt.includes('stark') || txt.includes('sehr hohe')) return iconCheck;
  if (txt.includes('kein bestandstreffer')) return iconQuestion;
  return iconInfo;
}

function getDistanceClass(km) {
  const d = Number(km);
  if (isNaN(d)) return 'dist-neutral';
  if (d <= 3) return 'dist-green';
  if (d <= 10) return 'dist-orange-light';
  if (d <= 30) return 'dist-orange';
  return 'dist-red';
}

function getDistanceLabel(km) {
  const d = Number(km);
  if (isNaN(d)) return `${km} km`;
  return `${d.toFixed(2)} km`;
}

function splitReasonMetric(reason) {
  const raw = String(reason || '').trim();
  const metricMatch = raw.match(/(\d+(?:[.,]\d+)?(?:\/\d+)?(?:\s?%|\s?km)?)/i);
  if (!metricMatch) return { label: raw, metric: '' };
  const metric = metricMatch[1].replace(/\s+/g, ' ').trim();
  const label = raw.replace(metricMatch[1], '').replace(/\s{2,}/g, ' ').replace(/[|:-]\s*$/g, '').trim();
  return { label: label || raw, metric };
}

function getMatchReasonRows(lead) {
  return getMatchReasons(lead).map((reason) => {
    const parts = splitReasonMetric(reason);
    return {
      ...parts,
      iconSrc: getReasonIconSrc(reason),
      tone: getReasonTone(reason, parts.metric),
    };
  });
}

function getLeadScoreDisplay(lead) {
  const score = Number(lead?.matching?.weightedScore);
  if (Number.isNaN(score)) return '--/100';
  return `${Math.round(score)}/100`;
}

const openQualityInfoKey = ref(null);

function getLeadQualityKey(lead) {
  return lead?.meta?.googlePlaces?.id || lead?.id || `${lead?.name || 'lead'}-${lead?.match?.meta?.gpNo || 'none'}`;
}

function isQualityInfoOpen(lead) {
  return openQualityInfoKey.value === getLeadQualityKey(lead);
}

function toggleQualityInfo(lead) {
  const key = getLeadQualityKey(lead);
  openQualityInfoKey.value = openQualityInfoKey.value === key ? null : key;
}

const qualityFilterCounts = computed(() => {
  const counts = { all: processedLeads.value.length, high: 0, medium: 0, low: 0, none: 0, errors: 0 };
  for (const lead of processedLeads.value) {
    const bucket = getQualityBucket(lead);
    counts[bucket] += 1;
  }
  counts.errors = processedLeads.value.filter(l => l.error === true).length;
  return counts;
});

const filteredProcessedLeads = computed(() => {
  if (qualityFilter.value === 'all') return processedLeads.value;
  if (qualityFilter.value === 'errors') return processedLeads.value.filter(l => l.error === true);
  return processedLeads.value.filter(lead => getQualityBucket(lead) === qualityFilter.value);
});

const noMatchLeads = computed(() => {
  return filteredProcessedLeads.value.filter(l => l.convertToBusinessPartner !== 'block' && !l.match);
});

const lowMatchLeads = computed(() => {
  return filteredProcessedLeads.value.filter(l => l.convertToBusinessPartner === 'allow-both' && !isOwnLead(l));
});

const highMatchLeads = computed(() => {
  return filteredProcessedLeads.value.filter(
    l => l.convertToBusinessPartner === 'allow-businessPartner' && !isDateUnassignActive(l.match?.status?.dateUnassign) && !isOwnLead(l)
  );
});


const noTakeoverLeads = computed(() => {
  // Exclude explicit error leads here; errors are shown in their own container
  return filteredProcessedLeads.value.filter(
    l => ((l.convertToBusinessPartner === 'block' || isDateUnassignActive(l.match?.status?.dateUnassign)) && !l.error) && !isOwnLead(l)
  );
});

const errorLeads = computed(() => {
  // Show error leads only when the Errors filter is active (or when viewing All)
  if (qualityFilter.value !== 'errors' && qualityFilter.value !== 'all') return [];
  return processedLeads.value.filter(l => l.error === true && !isOwnLead(l));
});

const ownMatchLeads = computed(() => {
  return filteredProcessedLeads.value.filter(l => isOwnLead(l));
});
</script>


<template>
  <i style="display: none;">{{leadGenerationStore.leadLists}}</i>
  
  <div class="reminder-wrapper singlepage-wrapper lead-generation max-width ">
    <div v-if="userStore.currentSelection.leadGeneration.search.isLoading === true" class="container">
        <div class="loader fullPage" id="loader"></div>
        <div class="loadingDescription">Daten werden geladen</div>
    </div>
    <!-- PreviewHeader and Query Dropdown outside widget-wrapper -->
    <main id="container-main">
      <div v-if="showConversionFeedback" class="conversion-feedback-overlay" @click.self="closeConversionFeedback">
        <div class="conversion-feedback-modal">
          <div class="conversion-feedback-header">
            <div class="conversion-feedback-title-wrap">
              <div class="conversion-feedback-success-icon" aria-hidden="true">✓</div>
              <div>
                <p class="conversion-feedback-eyebrow">Übernahme abgeschlossen</p>
                <h2>Feedback zur Eigenrecherche</h2>
                <p class="conversion-feedback-subline">Alle übernommenen Treffer wurden für den direkten Einstieg aufbereitet.</p>
              </div>
            </div>
            <button type="button" class="conversion-feedback-close" @click="closeConversionFeedback">Schließen</button>
          </div>

          <div class="conversion-feedback-summary">
            <div class="conversion-feedback-stat">
              <span class="conversion-feedback-stat-icon" aria-hidden="true">GP</span>
              <span class="conversion-feedback-stat-value">{{ createdBusinessPartners.length }}</span>
              <span class="conversion-feedback-stat-label">GPs übernommen</span>
            </div>
            <div class="conversion-feedback-stat">
              <span class="conversion-feedback-stat-icon" aria-hidden="true">i</span>
              <span class="conversion-feedback-stat-value">{{ conversionErrors.length }}</span>
              <span class="conversion-feedback-stat-label">Hinweise</span>
            </div>
            <div v-if="conversionFeedback?.opportunity" class="conversion-feedback-stat conversion-feedback-stat--wide">
              <span class="conversion-feedback-stat-icon" aria-hidden="true">OP</span>
              <span class="conversion-feedback-stat-label">Opportunity</span>
              <span class="conversion-feedback-stat-value conversion-feedback-stat-value--small">
                {{ conversionFeedback.opportunity.name }}<span v-if="conversionFeedback.opportunity.id"> #{{ conversionFeedback.opportunity.id }}</span>
              </span>
            </div>
          </div>

          <div class="conversion-feedback-actions">
            <button type="button" class="conversion-feedback-primary" @click="openConversionSearchList()">Zur Suchliste springen</button>
            <button type="button" class="conversion-feedback-secondary" @click="closeConversionFeedback">{{ conversionNextActionLabel }}</button>
          </div>

          <div v-if="createdBusinessPartners.length > 0" class="conversion-feedback-section">
            <h3>Übernommene Datensätze</h3>
            <div class="conversion-feedback-list">
              <div v-for="entry in createdBusinessPartners" :key="entry.id || entry.gpNo || entry.name" class="conversion-feedback-item">
                <div class="conversion-feedback-item-leading" aria-hidden="true"></div>
                <div class="conversion-feedback-item-content">
                  <div class="conversion-feedback-item-title">{{ entry.name || 'Unbenannt' }}</div>
                  <div class="conversion-feedback-item-meta">
                    <span v-if="entry.gpNo">GP {{ entry.gpNo }}</span>
                    <span v-if="entry.id">ID {{ entry.id }}</span>
                    <span v-if="isConversionEntryOpened(entry)" class="conversion-feedback-opened-pill">Geoeffnet</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="conversion-feedback-item-button"
                  @click="openBusinessPartnerFromConversion(entry)"
                >
                  <template v-if="resolveConversionBusinessPartnerGpNo(entry)">GP öffnen</template>
                  <template v-else>Kunde öffnen</template>
                </button>
              </div>
            </div>
          </div>

          <div v-if="conversionErrors.length > 0" class="conversion-feedback-section">
            <h3>Hinweise</h3>
            <ul class="conversion-feedback-errors" role="alert">
              <li v-for="(error, index) in conversionErrors" :key="`${error}-${index}`">{{ error }}</li>
            </ul>
          </div>
        </div>
      </div>

          <PreviewHeader
          :title="'Eigenrecherche'"
          :to="'/dashboard#lead-generation'"
          button-label="Zur Übersicht"
          arrow="left"
        />
        <div class="dropdown-list">
          <div v-if="selectedQuerySignature" class="dropdown-info">
            <template v-for="lead in leadGenerationStore.leadLists">
              <div v-if="lead.signature === selectedQuerySignature" class="dropdown-details">
                <div class="modern-loading-preview light-theme">
                  <div class="dropdown-header">
                    <label for="query-select" class="dropdown-label">Abfrage wählen</label>
                    <span class="dropdown-count">({{ leadGenerationStore.leadLists.length }} Abfragen)</span>
                  </div>
                  <select id="query-select" v-model="selectedQuerySignature" @change="handleSelectLeadList(selectedQuerySignature)" class="dropdown-select">
                    <option v-for="lead in leadGenerationStore.leadLists" :key="lead.signature" :value="lead.signature">
                      {{ formatSignature(lead.signature) }}
                    </option>
                  </select>
                  <div class="modern-loading-header">
                    <span class="dropdown-pill"><b>{{ lead.data.length }}</b> Treffer</span>
                    <span class="dropdown-pill dropdown-pill--loading" :class="{'dropdown-pill--loading': getProcessedCount(lead.data) < lead.data.length}">
                      <b>{{ getProcessedCount(lead.data) }}</b> verarbeitet
                    </span>
                    <span class="dropdown-pill dropdown-pill--potential">Potenzial: <b>{{ getPotentialCount }}</b></span>
                    <span
                      class="dropdown-pill dropdown-pill--capacity"
                      :class="capacityColorClass"
                    >Kapazität: <b>{{ eigenrechercheDisplay }}</b></span>
                    <span class="modern-loading-percent"><b>{{ loadingBarWidth }}</b>%</span>
                  </div>
                  <div class="modern-loading-bar-outer">
                    <div class="modern-loading-bar-inner" :style="{ width: loadingBarWidth + '%' }"></div>
                  </div>
                  <!-- <div class="modern-loading-numbers">
                    <span>Verarbeitet: {{ getProcessedCount(lead.data) }} / {{ lead.data.length }}</span>
                    <span v-if="lead.isLoading" class="modern-loading-label">Recherche läuft...</span>
                  </div> -->
                  <div class="buttons-wrapper">
                    <div class="buttons" style="display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; justify-content: space-between;">
                        <button :disabled="!canConvert" class="convertLeads" @click="handleConvertLeads()">
                          <template v-if="selectedLeadsCount > 0">
                            {{ selectedLeadsCount }} Treffer übernehmen
                          </template>
                          <template v-else>
                            0 Treffer übernehmen
                          </template>
                        </button>
                        <button class="convertLeads danger" @click="handleAbortConversion()">
                          Recherche löschen
                        </button>   
                    </div>
                    <div v-if="!canConvert" class="form-errors">
                      {{ convertHint }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      <div v-if="userStore.currentSelection.leadGeneration.isLoading" class="fullscreen-spinner-overlay">
        <transition name="fade">
          <div class="list-loading-overlay">
            <div class="spinner-center takeover-loading-card">
              <InlineSpinner size="lg" />
              <div class="loadingDescription loadingDescription--main">Wir übernehmen deine Recherche</div>
              <div class="loadingDescription loadingDescription--sub">Das kann einige Minuten dauern.</div>
              <div class="takeover-loading-info">
                Info: Wir crawlen dazu die Kundenwebsites und legen Branchen sowie GPs zu den ausgewählten Google Treffern an. Dadurch können wir bei diesen Firmen auch Terminbestätigungen verschicken. Das gilt für alle seit dem 09.09.2026 angelegten Eigenrecherche-Kunden. Für bereits früher übernommene Firmen klappt das leider noch nicht.
              </div>
            </div>
          </div>
        </transition>
      </div>
      <div>
        <div class="matching-quality-toolbar">
          <span class="quality-toolbar-title">Qualitätsfilter</span>
          <button type="button" :class="['quality-chip', { active: qualityFilter === 'all' }]" @click="qualityFilter = 'all'">Alle ({{ qualityFilterCounts.all }})</button>
          <button type="button" :class="['quality-chip', 'is-high', { active: qualityFilter === 'high' }]" @click="qualityFilter = 'high'">Score ({{ qualityFilterCounts.high }})</button>
          <button type="button" :class="['quality-chip', 'is-medium', { active: qualityFilter === 'medium' }]" @click="qualityFilter = 'medium'">Score ({{ qualityFilterCounts.medium }})</button>
          <button type="button" :class="['quality-chip', 'is-low', { active: qualityFilter === 'low' }]" @click="qualityFilter = 'low'">Unklar ({{ qualityFilterCounts.low }})</button>
          <button type="button" :class="['quality-chip', 'is-none', { active: qualityFilter === 'none' }]" @click="qualityFilter = 'none'">Kein Match ({{ qualityFilterCounts.none }})</button>
          <button type="button" :class="['quality-chip', 'is-error', { active: qualityFilter === 'errors' }]" @click="qualityFilter = 'errors'">Errors ({{ qualityFilterCounts.errors }})</button>
        </div>
        <div class="widget-wrapper lead-generation">
          <!-- <router-link to="/dashboard#potential-bk">
            <div class="headline-wrapper">
              <h2>Leads aus Google-Recherche</h2>
              <p>{{ userStore.currentSelection.leadGeneration.search.query }}, matching {{ leadGenerationStore.leadList?.data?.filter((lead) => lead.processed===true).length }}/ {{ leadGenerationStore.leadList?.data.length }}</p>
            </div>
          </router-link> -->
          <div class="container-wrapper">
            <!-- Hohes Matching auf Nichtkunde: Nur Treffer, bei denen Google-Checkbox deaktiviert und Matching-Checkbox deaktiviert ist -->
            <div class="container" v-if="noMatchLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Kein Matching auf Bestand <span class="container-count">{{ noMatchLeads.length }}</span></h2>
              <p>Google-Treffer, die übernommen werden können.</p>
              <div v-if="isAtCapacity" class="form-errors" style="margin-bottom:1rem;">Kapazitätslimit erreicht: Bitte bearbeiten Sie zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu erhalten.</div>
              <div class="table-responsive google-leads-table">
                <table class="sticky-table">
                  <thead>
                    <tr>
                        <th class="col-highlightBorder">Sales Manager</th>
                        <th class="col-select"></th>
                        <th class="col-name">Name / Firma</th>
                        <th class="col-info">Info</th>
                        <th class="col-address">Adresse</th>
                        <th class="col-phone-numbers">Telefon</th>
                        <th class="col-urls">Url</th>
                    </tr>
                  </thead>
                    <!-- Area with businessPartners that have matches => show lead and match -->
                    <tbody>
                      <tr>
                        <td class="col-highlightBorder"></td>
                        <td class="col-select">
                          <input
                            type="checkbox"
                            :checked="areAllGoogleLeadsSelected"
                            @change="toggleAllGoogleLeads($event)"
                            :disabled="isAtCapacity && !areAllGoogleLeadsSelected"
                          />
                        </td>
                        <td class="col-name" colspan="5">Alle auswählen</td>
                      </tr>
                    </tbody>
                    <template v-for="(lead, index) in noMatchLeads.sort((a,b) => (a.matching?.score || 0) - (b.matching?.score || 0))" :key="lead.id">
                      <!-- first row: the lead that was found by the google places search-->
                      <tr class="spacer"><td colspan="7"></td></tr>
                      <!-- <tr v-if="index === 0" class="spacer"><td colspan="6">Erlaubt</td></tr> -->
                      <tbody :class="['google', lead.convertToBusinessPartnerColor]" :title="getMatchExplanationTitle(lead)" @mouseover="handleCompareLeadToBusinessPartners(lead)" @mouseleave="clearLeadHover">
                        <tr>
                          <td class="col-highlightBorder no-sales-manager-cell">
                                    <div class="selection-quality-corner">
                                      <span :class="['selection-quality-badge', 'is-' + getQualityBucket(lead)]">{{ getQualityBucketLabel(getQualityBucket(lead)) }}</span>
                                      <button
                                        type="button"
                                        class="selection-quality-info-btn"
                                        @click.stop="toggleQualityInfo(lead)"
                                        :aria-expanded="isQualityInfoOpen(lead)"
                                        aria-label="Begründung anzeigen"
                                      >i</button>
                                      <div v-if="isQualityInfoOpen(lead)" class="selection-quality-info-panel">
                                        <div class="selection-quality-panel-head">
                                          <span class="selection-quality-panel-title">Score</span>
                                          <span :class="['selection-quality-panel-score', `is-${getQualityBucket(lead)}`]">{{ getLeadScoreDisplay(lead) }}</span>
                                        </div>
                                        <hr class="selection-quality-panel-separator" />
                                        <div class="selection-quality-info-list">
                                          <div
                                            v-for="(row, rIndex) in getMatchReasonRows(lead)"
                                            :key="`${getLeadQualityKey(lead)}-reason-${rIndex}`"
                                            class="selection-quality-info-item"
                                          >
                                            <img :src="row.iconSrc" class="selection-quality-info-icon" alt="" aria-hidden="true" />
                                            <span class="selection-quality-info-text">{{ row.label }}</span>
                                            <span v-if="row.metric" :class="['selection-quality-metric-pill', `is-${row.tone}`]">{{ row.metric }}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <span class="no-sales-manager-placeholder">-</span>
                          </td>
                          <td class="col-select">
                            <!-- <span class="highlightBorder"></span>                -->
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.google" @click="handleSelectLead(lead, 'google')" :disabled="isSelectionDisabledAtCapacity(lead, 'google') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-lead' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <!-- <td class="col-similarity-indicator" rowspan="2"><img  class="imgWrapper" :class="{'inactive' : !businessPartner.match.name }" src="@/assets/imgs/icons/shuffle-solid.svg"></td> -->
                          <td class="col-name">
                            <!-- <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations"><img src="@/assets/imgs/icons/star-border-solid.svg">{{lead.meta.googlePlaces?.ratings?.count}}</div>
                              <div class="googleEvaluations">⌀ {{lead.meta.googlePlaces?.ratings?.average}}</div>
                              <div class="googleEvaluations">Neueste {{lead.meta.googlePlaces?.ratings?.dateLastRating}}</div>
                            </div> -->
                            <a class="button"
                                  target="_blank" 
                                  :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)"
                              >
                              <div v-if="!lead.matching?.name || !lead.match || !isLeadHovered(lead)">
                                <!-- <div class="logoWrapper"><img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google Logo"> <p>{{lead.meta.googlePlaces.name}}</p></div> -->
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <p>{{lead.name}}</p></div>
                              </div>
                              <div v-else>
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <div>
                                    <p v-for="(token, index) in lead.matching.name.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</p>
                                  </div>
                                </div>
                              </div>
                            </a>
                          </td>
                          <td class="col-info">
                            <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations">{{formatMonthYear(lead.meta.googlePlaces?.ratings?.dateLastRating)}}</div>
                              <div class="rating-wrapper">
                                <template v-for="(star, index) in renderStars(lead.meta.googlePlaces?.ratings?.average || 0)" :key="index" >
                                  <img class="yellow" v-if="star === 'yellow'" src="@/assets/imgs/icons/star-border-solid.svg" alt="yellow" />
                                  <img class="grey" v-else src="@/assets/imgs/icons/star-border-solid-black.svg" alt="black" />
                                </template>
                              </div>                              
                              <div class="googleEvaluations">({{lead.meta.googlePlaces?.ratings?.count}})</div>
                            </div>
                          </td>
                          <td class="col-address">
                            <div v-if="!lead.matching?.address|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.address?.split(',').splice(0,2).join(", ")}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.address.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-phone-numbers">
                            <div v-if="!lead.matching?.phoneNumbers|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.phoneNumbers?.join(' | ') || ''}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.phoneNumbers.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-urls">
                            <a v-if="lead.meta.googlePlaces.urls" class="button"
                                  target="_blank" 
                                  :href=lead.meta.googlePlaces.urls[0]
                              >
                              <div v-if="!lead.matching?.urls|| !lead.match || !isLeadHovered(lead)">
                                {{lead.meta.googlePlaces.urls?.join(' | ')|| ''}}
                              </div>
                              <div v-else>
                                <span v-for="(token, index) in lead.matching.urls.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}</span>
                              </div>  
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                </table>
              </div>
            </div>
            <!-- Niedriges Matching auf Nichtkunde: Nur Treffer, bei denen beide Checkboxen aktiv wären (allow-both) -->
            <div class="container" v-if="lowMatchLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Unsicheres Matching auf Nichtkunden <span class="container-count">{{ lowMatchLeads.length }}</span></h2>
              <p>Vergleiche die vorgeschlagenen Treffer. Um doppelte Datensätze zu vermeiden, übernehme nach Möglichkeit den bestehenden Geschäftspartner. Ist dieser nicht passend, wähle den Google-Treffer.</p>
              <div v-if="isAtCapacity" class="form-errors" style="margin-bottom:1rem;">Kapazitätslimit erreicht: Bitte bearbeiten Sie zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu erhalten.</div>
              <div class="table-responsive">
                <table class="sticky-table">
                  <thead>
                      <tr>
                        <th class="col-highlightBorder">Sales Manager</th>
                        <th class="col-select"></th>
                        <th class="col-name">Name / Firma</th>
                        <th class="col-info">Info</th>
                        <th class="col-address">Adresse</th>
                        <th class="col-phone-numbers">Telefon</th>
                        <th class="col-urls">Url</th>
                      </tr>
                  </thead>
                    <!-- Area with businessPartners that have matches => show lead and match -->
                    <template v-for="(lead, index) in lowMatchLeads.sort((a,b) => (a.matching?.score || 0) - (b.matching?.score || 0))" :key="lead.id">
                      <!-- first row: the lead that was found by the google places search-->
                      <tr class="spacer"><td colspan="7"></td></tr>
                      <!-- <tr v-if="index === 0" class="spacer"><td colspan="7">Erlaubt</td></tr> -->
                      <tbody :class="lead.convertToBusinessPartnerColor" :title="getMatchExplanationTitle(lead)" @mouseover="handleCompareLeadToBusinessPartners(lead)" @mouseleave="clearLeadHover">
                        <tr>
                          <td rowspan="2">
                            <userAvatar
                              :size="48"
                              :showSalesAgent="true"
                              :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                              :idUser="lead.match?.status?.salesAgent?.idUser"
                              :statusOverride="(!lead.match?.status?.salesAgent || lead.match?.status?.salesAgent.firstName === 'Dummy') ? { name: 'Nicht zugewiesen', status: getStatusSubline(lead.match?.status) } : undefined"
                            >
                              <template #default>
                                <template v-if="lead.match?.status?.dateUnassign && isDateUnassignActive(lead.match?.status?.dateUnassign)">
                                  <span class="status-label status-label--yellow status-label--yellow-vertical">
                                    <span class="date status-label-yellow-date">
                                      Freigabe am {{ lead.match?.status?.dateUnassign ? new Date(lead.match?.status?.dateUnassign).toLocaleDateString('de-DE') : '' }}
                                    </span>
                                    <CountdownBadge :dateUnassign="lead.match?.status?.dateUnassign" />
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedManually">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Manuell blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedAutomatically">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Automatisch blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.capacityBlocked">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Kapazität erreicht
                                  </span>
                                </template>
                                <template v-else-if="hasActiveContracts(lead.match?.status)">
                                  <span class="status-label status-label--indigo">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Bestandskunde
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.salesAgent && lead.match?.status?.salesAgent.firstName !== 'Dummy'">
                                  <span v-if="isLockedAssignedStatus(lead.match?.status)" class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                  <span v-else class="status-label status-label--green">
                                    <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                </template>
                                <template v-else>
                                  <span class="status-label status-label--green">
                                    <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                    Übernahme möglich
                                  </span>
                                </template>
                              </template>
                            </userAvatar>
                            <div class="selection-quality-corner">
                              <span :class="['selection-quality-badge', 'is-' + getQualityBucket(lead)]">{{ getQualityBucketLabel(getQualityBucket(lead)) }}</span>
                              <button
                                type="button"
                                class="selection-quality-info-btn"
                                @click.stop="toggleQualityInfo(lead)"
                                :aria-expanded="isQualityInfoOpen(lead)"
                                aria-label="Begründung anzeigen"
                              >i</button>
                              <div v-if="isQualityInfoOpen(lead)" class="selection-quality-info-panel">
                                <div class="selection-quality-panel-head">
                                  <span class="selection-quality-panel-title">Score</span>
                                  <span :class="['selection-quality-panel-score', `is-${getQualityBucket(lead)}`]">{{ getLeadScoreDisplay(lead) }}</span>
                                </div>
                                <hr class="selection-quality-panel-separator" />
                                <div class="selection-quality-info-list">
                                  <div
                                    v-for="(row, rIndex) in getMatchReasonRows(lead)"
                                    :key="`${getLeadQualityKey(lead)}-reason-${rIndex}`"
                                    class="selection-quality-info-item"
                                  >
                                    <img :src="row.iconSrc" class="selection-quality-info-icon" alt="" aria-hidden="true" />
                                    <span class="selection-quality-info-text">{{ row.label }}</span>
                                    <span v-if="row.metric" :class="['selection-quality-metric-pill', `is-${row.tone}`]">{{ row.metric }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="col-select">
                            <!-- <span class="highlightBorder"></span>                -->
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.google" @click="handleSelectLead(lead, 'google')" :disabled="isSelectionDisabledAtCapacity(lead, 'google') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-lead' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <!-- <td class="col-similarity-indicator" rowspan="2"><img  class="imgWrapper" :class="{'inactive' : !businessPartner.match.name }" src="@/assets/imgs/icons/shuffle-solid.svg"></td> -->
                          <td class="col-name">
                            <!-- <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations"><img src="@/assets/imgs/icons/star-border-solid.svg">{{lead.meta.googlePlaces?.ratings?.count}}</div>
                              <div class="googleEvaluations">⌀ {{lead.meta.googlePlaces?.ratings?.average}}</div>
                              <div class="googleEvaluations">Neueste {{lead.meta.googlePlaces?.ratings?.dateLastRating}}</div>
                            </div> -->
                            <a class="button"
                                  target="_blank" 
                                  :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)"
                              >
                              <div v-if="!lead.matching?.name || !lead.match || !isLeadHovered(lead)">
                                <!-- <div class="logoWrapper"><img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google Logo"> <p>{{lead.meta.googlePlaces.name}}</p></div> -->
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <p>{{lead.name}}</p></div>
                              </div>
                              <div v-else>
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <div>
                                    <p v-for="(token, index) in lead.matching.name.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</p>
                                  </div>
                                </div>
                              </div>
                            </a>
                          </td>
                          <td class="col-info">
                            <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations">{{formatMonthYear(lead.meta.googlePlaces?.ratings?.dateLastRating)}}</div>
                              <div class="rating-wrapper">
                                <template v-for="(star, index) in renderStars(lead.meta.googlePlaces?.ratings?.average || 0)" :key="index" >
                                  <img class="yellow" v-if="star === 'yellow'" src="@/assets/imgs/icons/star-border-solid.svg" alt="yellow" />
                                  <img class="grey" v-else src="@/assets/imgs/icons/star-border-solid-black.svg" alt="black" />
                                </template>
                              </div>                              
                              <div class="googleEvaluations">({{lead.meta.googlePlaces?.ratings?.count}})</div>
                            </div>
                          </td>
                          <td class="col-address">
                            <div v-if="!lead.matching?.address|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.address?.split(',').splice(0,2).join(", ")}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.address.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-phone-numbers">
                            <div v-if="!lead.matching?.phoneNumbers|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.phoneNumbers?.join(' | ') || ''}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.phoneNumbers.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-urls">
                            <a v-if="lead.meta.googlePlaces.urls" class="button"
                                  target="_blank" 
                                  :href=lead.meta.googlePlaces.urls[0]
                              >
                              <div v-if="!lead.matching?.urls|| !lead.match || !isLeadHovered(lead)">
                                {{lead.meta.googlePlaces.urls?.join(' | ')|| ''}}
                              </div>
                              <div v-else>
                                <span v-for="(token, index) in lead.matching.urls.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}</span>
                              </div>  
                            </a>
                          </td>
                        </tr>
                        <tr v-if="lead.match" class="match">
                          <td class="col-select">          
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.businessPartner" @click="handleSelectLead(lead, 'businessPartner')" :disabled="isSelectionDisabledAtCapacity(lead, 'businessPartner') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-businessPartner' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <td class="col-name">
                            <div class="logoWrapper">
                              <img src="@/assets/imgs/logos/sellwerk/sellwerk-favicon.svg" alt="Sellwerk Logo">
                                <span>{{lead.match.name}}</span>
                                <span class="gp-pill" :title="lead.match.confidence">{{ lead.match.meta.gpNo }}</span>
                            </div>
                          </td>
                          <td class="col-info">
                            <span v-if="lead.match?.status?.contracts && lead.match.status?.contracts !== '--- -- -- --'">
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('GS')) }"
                                    src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Abo in GS" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('TB')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Abo im TB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('OE')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Abo im ÖTB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <div :class="{ 'active': (lead.match.status.contracts?.includes('OMP')) }" class="omp">omp</div>
                                </div>
                              </div>
                            </span>
                          </td>
                          <td class="col-address">
                            <div class="address-distance-cell">
                              <span>{{lead.match.meta.normalization?.address?.split(',').splice(0,2).join(", ")}}</span>
                              <span :class="['distance-pill', getDistanceClass(lead.match.distance)]">{{ getDistanceLabel(lead.match.distance) }}</span>
                            </div>
                          </td>
                          <td class="col-phone-numbers">
                            <div>{{lead.match.meta.normalization?.phoneNumbers?.join(' | ') || ''}}</div>
                          </td>
                          <td class="col-urls">
                            <div>{{lead.match.meta.normalization?.urls?.join(' | ')|| ''}}</div>
                          </td>
                        </tr>
                        <tr v-else class="noMatch">
                          <td colspan="7">
                            kein matchender GP gefunden 
                          </td>
                        </tr>
                      </tbody>
                    </template>
                </table>
              </div>
            </div>
            <!-- Hohes Matching auf unbearbeitete Nichtkunden: Nur Treffer, bei denen nur die BusinessPartner-Checkbox aktiv wäre (allow-businessPartner) -->
            <div class="container" v-if="highMatchLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Hohes Matching auf unbearbeitete Nichtkunden <span class="container-count">{{ highMatchLeads.length }}</span></h2>
              <p>Übernahme nur aus dem Bestand (Eigene Potentiale werden nicht angezeigt)</p>
              <div v-if="isAtCapacity" class="form-errors" style="margin-bottom:1rem;">Kapazitätslimit erreicht: Bitte bearbeiten Sie zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu erhalten.</div>
              <div class="table-responsive">
                <table class="sticky-table">
                  <thead>
                      <tr>
                        <th class="col-highlightBorder">Sales Manager</th>
                        <th class="col-select"></th>
                        <th class="col-name">Name / Firma</th>
                        <th class="col-info">Info</th>
                        <th class="col-address">Adresse</th>
                        <th class="col-phone-numbers">Telefon</th>
                        <th class="col-urls">Url</th>
                      </tr>
                  </thead>
                    <!-- Area with businessPartners that have matches => show lead and match -->
                    <template v-for="(lead, index) in highMatchLeads.sort((a,b) => (a.matching?.score || 0) - (b.matching?.score || 0))" :key="lead.id">
                      <!-- first row: the lead that was found by the google places search-->
                      <tr class="spacer"><td colspan="7"></td></tr>
                      <!-- <tr v-if="index === 0" class="spacer"><td colspan="7">Erlaubt</td></tr> -->
                      <tbody :class="lead.convertToBusinessPartnerColor" :title="getMatchExplanationTitle(lead)" @mouseover="handleCompareLeadToBusinessPartners(lead)" @mouseleave="clearLeadHover">
                        <tr>
                          <td rowspan="2">
                            <userAvatar
                              :size="48"
                              :showSalesAgent="true"
                              :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                              :idUser="lead.match?.status?.salesAgent?.idUser"
                              :statusOverride="(!lead.match?.status?.salesAgent || lead.match?.status?.salesAgent.firstName === 'Dummy') ? { name: 'Nicht zugewiesen', status: getStatusSubline(lead.match?.status) } : undefined"
                            >
                              <template #default>
                                <template v-if="lead.match?.status?.dateUnassign && isDateUnassignActive(lead.match?.status?.dateUnassign)">
                                  <span class="status-label status-label--yellow status-label--yellow-vertical">
                                    <span class="date status-label-yellow-date">
                                      Freigabe am {{ lead.match?.status?.dateUnassign ? new Date(lead.match?.status?.dateUnassign).toLocaleDateString('de-DE') : '' }}
                                    </span>
                                    <CountdownBadge :dateUnassign="lead.match?.status?.dateUnassign" />
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedManually">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Manuell blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedAutomatically">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Automatisch blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.capacityBlocked">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Kapazität erreicht
                                  </span>
                                </template>
                                <template v-else-if="hasActiveContracts(lead.match?.status)">
                                  <span class="status-label status-label--indigo">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Bestandskunde
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.salesAgent && lead.match?.status?.salesAgent.firstName !== 'Dummy'">
                                  <span v-if="isLockedAssignedStatus(lead.match?.status)" class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                  <span v-else class="status-label status-label--green">
                                    <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                </template>
                                <template v-else>
                                  <span class="status-label status-label--green">
                                    <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                    Übernahme möglich
                                  </span>
                                </template>
                              </template>
                            </userAvatar>
                            <div class="selection-quality-corner">
                              <span :class="['selection-quality-badge', 'is-' + getQualityBucket(lead)]">{{ getQualityBucketLabel(getQualityBucket(lead)) }}</span>
                              <button
                                type="button"
                                class="selection-quality-info-btn"
                                @click.stop="toggleQualityInfo(lead)"
                                :aria-expanded="isQualityInfoOpen(lead)"
                                aria-label="Begründung anzeigen"
                              >i</button>
                              <div v-if="isQualityInfoOpen(lead)" class="selection-quality-info-panel">
                                <div class="selection-quality-panel-head">
                                  <span class="selection-quality-panel-title">Score</span>
                                  <span :class="['selection-quality-panel-score', `is-${getQualityBucket(lead)}`]">{{ getLeadScoreDisplay(lead) }}</span>
                                </div>
                                <hr class="selection-quality-panel-separator" />
                                <div class="selection-quality-info-list">
                                  <div
                                    v-for="(row, rIndex) in getMatchReasonRows(lead)"
                                    :key="`${getLeadQualityKey(lead)}-reason-${rIndex}`"
                                    class="selection-quality-info-item"
                                  >
                                    <img :src="row.iconSrc" class="selection-quality-info-icon" alt="" aria-hidden="true" />
                                    <span class="selection-quality-info-text">{{ row.label }}</span>
                                    <span v-if="row.metric" :class="['selection-quality-metric-pill', `is-${row.tone}`]">{{ row.metric }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="col-select">
                            <!-- <span class="highlightBorder"></span>                -->
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.google" @click="handleSelectLead(lead, 'google')" :disabled="isSelectionDisabledAtCapacity(lead, 'google') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-lead' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <!-- <td class="col-similarity-indicator" rowspan="2"><img  class="imgWrapper" :class="{'inactive' : !businessPartner.match.name }" src="@/assets/imgs/icons/shuffle-solid.svg"></td> -->
                          <td class="col-name">
                            <!-- <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations"><img src="@/assets/imgs/icons/star-border-solid.svg">{{lead.meta.googlePlaces?.ratings?.count}}</div>
                              <div class="googleEvaluations">⌀ {{lead.meta.googlePlaces?.ratings?.average}}</div>
                              <div class="googleEvaluations">Neueste {{lead.meta.googlePlaces?.ratings?.dateLastRating}}</div>
                            </div> -->
                            <a class="button"
                                  target="_blank" 
                                  :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)"
                              >
                              <div v-if="!lead.matching?.name || !lead.match || !isLeadHovered(lead)">
                                <!-- <div class="logoWrapper"><img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google Logo"> <p>{{lead.meta.googlePlaces.name}}</p></div> -->
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <p>{{lead.name}}</p></div>
                              </div>
                              <div v-else>
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <div>
                                    <p v-for="(token, index) in lead.matching.name.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</p>
                                  </div>
                                </div>
                              </div>
                            </a>
                          </td>
                          <td class="col-info">
                            <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations">{{formatMonthYear(lead.meta.googlePlaces?.ratings?.dateLastRating)}}</div>
                              <div class="rating-wrapper">
                                <template v-for="(star, index) in renderStars(lead.meta.googlePlaces?.ratings?.average || 0)" :key="index" >
                                  <img class="yellow" v-if="star === 'yellow'" src="@/assets/imgs/icons/star-border-solid.svg" alt="yellow" />
                                  <img class="grey" v-else src="@/assets/imgs/icons/star-border-solid-black.svg" alt="black" />
                                </template>
                              </div>                              
                              <div class="googleEvaluations">({{lead.meta.googlePlaces?.ratings?.count}})</div>
                            </div>
                          </td>
                          <td class="col-address">
                            <div v-if="!lead.matching?.address|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.address?.split(',').splice(0,2).join(", ")}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.address.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-phone-numbers">
                            <div v-if="!lead.matching?.phoneNumbers|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.phoneNumbers?.join(' | ') || ''}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.phoneNumbers.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-urls">
                            <a v-if="lead.meta.googlePlaces.urls" class="button"
                                  target="_blank" 
                                  :href=lead.meta.googlePlaces.urls[0]
                              >
                              <div v-if="!lead.matching?.urls|| !lead.match || !isLeadHovered(lead)">
                                {{lead.meta.googlePlaces.urls?.join(' | ')|| ''}}
                              </div>
                              <div v-else>
                                <span v-for="(token, index) in lead.matching.urls.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}</span>
                              </div>  
                            </a>
                          </td>
                        </tr>
                        <tr v-if="lead.match" class="match">
                          <td class="col-select">          
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.businessPartner" @click="handleSelectLead(lead, 'businessPartner')" :disabled="isSelectionDisabledAtCapacity(lead, 'businessPartner') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-businessPartner' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <td class="col-name">
                            <div class="logoWrapper">
                              <img src="@/assets/imgs/logos/sellwerk/sellwerk-favicon.svg" alt="Sellwerk Logo">
                              
                                <span>{{lead.match.name}}</span>
                                <span class="gp-pill" :title="lead.match.confidence">{{ lead.match.meta.gpNo }}</span>
                                
                            </div>
                          </td>
                          <td class="col-info">
                            <span v-if="lead.match?.status?.contracts && lead.match.status?.contracts !== '--- -- -- --'">
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('GS')) }"
                                    src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Abo in GS" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('TB')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Abo im TB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('OE')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Abo im ÖTB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <div :class="{ 'active': (lead.match.status.contracts?.includes('OMP')) }" class="omp">omp</div>
                                </div>
                              </div>
                            </span>
                          </td>
                          <td class="col-address">
                            <div class="address-distance-cell">
                              <span>{{lead.match.meta.normalization?.address?.split(',').splice(0,2).join(", ")}}</span>
                              <span :class="['distance-pill', getDistanceClass(lead.match.distance)]">{{ getDistanceLabel(lead.match.distance) }}</span>
                            </div>
                          </td>
                          <td class="col-phone-numbers">
                            <div>{{lead.match.meta.normalization?.phoneNumbers?.join(' | ') || ''}}</div>
                          </td>
                          <td class="col-urls">
                            <div>{{lead.match.meta.normalization?.urls?.join(' | ')|| ''}}</div>
                          </td>
                        </tr>
                        <tr v-else class="noMatch">
                          <td colspan="7">
                            kein matchender GP gefunden 
                          </td>
                        </tr>
                      </tbody>
                    </template>
                </table>
              </div>
            </div>
            <!-- Eigene Kunden: Treffer bereits dem eingeloggten User zugeordnet – nicht übernehmbar, aber anklickbar -->
            <div class="container" v-if="ownMatchLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Bereits in deinem Potential <span class="container-count">{{ ownMatchLeads.length }}</span></h2>
              <p>Diese Treffer sind dir bereits zugeordnet. Eine Übernahme ist unnötig.</p>
              <div class="table-responsive">
                <table class="sticky-table">
                  <thead>
                    <tr>
                      <th class="col-highlightBorder">Sales Manager</th>
                      <th class="col-select"></th>
                      <th class="col-name">Name / Firma</th>
                      <th class="col-info">Info</th>
                      <th class="col-address">Adresse</th>
                      <th class="col-phone-numbers">Telefon</th>
                      <th class="col-urls">Url</th>
                    </tr>
                  </thead>
                  <template v-for="(lead, index) in ownMatchLeads
                    .sort((a, b) => {
                      // Countdown Timer Treffer nach oben
                      const aCountdown = a.match?.status?.dateUnassign && isDateUnassignActive(a.match?.status?.dateUnassign);
                      const bCountdown = b.match?.status?.dateUnassign && isDateUnassignActive(b.match?.status?.dateUnassign);
                      if (aCountdown && !bCountdown) return -1;
                      if (!aCountdown && bCountdown) return 1;
                      return (a.matching?.score || 0) - (b.matching?.score || 0);
                    })" :key="lead.id">
                    <tr class="spacer"><td colspan="7"></td></tr>
                    <tbody class="own-lead unavailable" :title="getMatchExplanationTitle(lead)" @mouseover="handleCompareLeadToBusinessPartners(lead)" @mouseleave="clearLeadHover">
                      <tr>
                        <td rowspan="2">
                          <userAvatar
                            :size="48"
                            :showSalesAgent="true"
                            :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                            :idUser="lead.match?.status?.salesAgent?.idUser"
                            :statusOverride="(!lead.match?.status?.salesAgent || lead.match?.status?.salesAgent.firstName === 'Dummy') ? { name: 'Nicht zugewiesen', status: getStatusSubline(lead.match?.status) } : undefined"
                          >
                            <template #default>
                              <template v-if="lead.match?.status?.dateUnassign && isDateUnassignActive(lead.match?.status?.dateUnassign)">
                                <span class="status-label status-label--yellow status-label--yellow-vertical">
                                  <span class="date status-label-yellow-date">
                                    Freigabe am {{ lead.match?.status?.dateUnassign ? new Date(lead.match?.status?.dateUnassign).toLocaleDateString('de-DE') : '' }}
                                  </span>
                                  <CountdownBadge :dateUnassign="lead.match?.status?.dateUnassign" />
                                </span>
                              </template>
                              <template v-else-if="lead.match?.status?.blockedManually">
                                <span class="status-label status-label--red">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  Manuell blockiert
                                </span>
                              </template>
                              <template v-else-if="lead.match?.status?.blockedAutomatically">
                                <span class="status-label status-label--red">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  Automatisch blockiert
                                </span>
                              </template>
                              <template v-else-if="lead.match?.status?.capacityBlocked">
                                <span class="status-label status-label--red">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  Kapazität erreicht
                                </span>
                              </template>
                              <template v-else-if="hasActiveContracts(lead.match?.status)">
                                <span class="status-label status-label--indigo">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  Bestandskunde
                                </span>
                              </template>
                              <template v-else-if="lead.match?.status?.salesAgent && lead.match?.status?.salesAgent.firstName !== 'Dummy'">
                                <span v-if="isLockedAssignedStatus(lead.match?.status)" class="status-label status-label--red">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  {{ getAssignedStatusLabel(lead.match?.status) }}
                                </span>
                                <span v-else class="status-label status-label--green">
                                  <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                  {{ getAssignedStatusLabel(lead.match?.status) }}
                                </span>
                              </template>
                              <template v-else>
                                <span class="status-label status-label--red">
                                  <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                  Nicht übernehmbar
                                </span>
                              </template>
                            </template>
                          </userAvatar>
                        </td>
                        <td class="col-select">
                          <input type="checkbox" disabled class="own-disabled-checkbox" />
                        </td>
                        <td class="col-name">
                          <a class="button" target="_blank" :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)">
                            <div v-if="!lead.matching?.name || !lead.match || !isLeadHovered(lead)" class="logoWrapper google">
                              <img src="@/assets/imgs/logos/google/Google__G__logo.png" :title="`Bewertungen: ${lead.meta.googlePlaces?.ratings?.count}, ⌀ ${lead.meta.googlePlaces?.ratings?.average}`" />
                              <p>{{ lead.name }}</p>
                            </div>
                            <div v-else class="logoWrapper google">
                              <img src="@/assets/imgs/logos/google/Google__G__logo.png" :title="`Bewertungen: ${lead.meta.googlePlaces?.ratings?.count}, ⌀ ${lead.meta.googlePlaces?.ratings?.average}`" />
                              <div>
                                <p v-for="(token, tokenIndex) in lead.matching.name.tokens" :key="tokenIndex" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</p>
                              </div>
                            </div>
                          </a>
                        </td>
                        <td class="col-info">
                          <div class="googleEvaluationsWrapper">
                            <div class="googleEvaluations">{{ formatMonthYear(lead.meta.googlePlaces?.ratings?.dateLastRating) }}</div>
                            <div class="rating-wrapper">
                              <template v-for="(star, index) in renderStars(lead.meta.googlePlaces?.ratings?.average || 0)" :key="index" >
                                <img class="yellow" v-if="star === 'yellow'" src="@/assets/imgs/icons/star-border-solid.svg" alt="yellow" />
                                <img class="grey" v-else src="@/assets/imgs/icons/star-border-solid-black.svg" alt="black" />
                              </template>
                            </div>
                            <div class="googleEvaluations">({{ lead.meta.googlePlaces?.ratings?.count }})</div>
                          </div>
                        </td>
                        <td class="col-address">
                          <div v-if="!lead.matching?.address || !lead.match || !isLeadHovered(lead)">
                            {{ lead.meta.googlePlaces.address?.split(',').splice(0,2).join(", ") }}
                          </div>
                          <div v-else>
                            <span v-for="(token, tokenIndex) in lead.matching.address.tokens" :key="tokenIndex" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                          </div>
                        </td>
                        <td class="col-phone-numbers">
                          <div v-if="!lead.matching?.phoneNumbers || !lead.match || !isLeadHovered(lead)">
                            {{ lead.meta.googlePlaces.phoneNumbers?.join(' | ') || '' }}
                          </div>
                          <div v-else>
                            <span v-for="(token, tokenIndex) in lead.matching.phoneNumbers.tokens" :key="tokenIndex" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                          </div>
                        </td>
                        <td class="col-urls">
                          <a v-if="lead.meta.googlePlaces.urls" target="_blank" :href="lead.meta.googlePlaces.urls[0]" class="button">
                            <div v-if="!lead.matching?.urls || !lead.match || !isLeadHovered(lead)">
                              {{ lead.meta.googlePlaces.urls?.join(' | ') || '' }}
                            </div>
                            <div v-else>
                              <span v-for="(token, tokenIndex) in lead.matching.urls.tokens" :key="tokenIndex" :class="{'indicate-similarity': token.match}">{{token.token}}</span>
                            </div>
                          </a>
                        </td>
                      </tr>
                      <tr v-if="lead.match" class="match">
                        <td class="col-select">
                          <input type="checkbox" disabled class="own-disabled-checkbox" />
                        </td>
                        <td class="col-name">
                          <div class="logoWrapper">
                            <img src="@/assets/imgs/logos/sellwerk/sellwerk-favicon.svg" alt="Sellwerk Logo" />
                            <router-link
                              :to="{ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: lead.match.meta.gpNo, fromResearchList: 'true' } }"
                              class="own-lead-bp-link"
                            >{{ lead.match.name }}</router-link>
                            <span class="gp-pill" :title="lead.match.confidence">{{ lead.match.meta.gpNo }}</span>
                          </div>
                        </td>
                        <td class="col-info">
                          <span v-if="lead.match?.status?.contracts && lead.match.status?.contracts !== '--- -- -- --'">
                            <div class="productsIcon-wrapper">
                              <div class="indicator-wrapper"><img :class="{ 'active': lead.match.status.contracts?.includes('GS') }" src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="GS" /></div>
                              <div class="indicator-wrapper"><img :class="{ 'active': lead.match.status.contracts?.includes('TB') }" src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="TB" /></div>
                              <div class="indicator-wrapper"><img :class="{ 'active': lead.match.status.contracts?.includes('OE') }" src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="OE" /></div>
                              <div class="indicator-wrapper"><div :class="{ 'active': lead.match.status.contracts?.includes('OMP') }" class="omp">omp</div></div>
                            </div>
                          </span>
                        </td>
                        <td class="col-address">
                          <div class="address-distance-cell">
                            <span>{{ lead.match.meta.normalization?.address?.split(',').splice(0,2).join(", ") }}</span>
                            <span :class="['distance-pill', getDistanceClass(lead.match.distance)]">{{ getDistanceLabel(lead.match.distance) }}</span>
                          </div>
                        </td>
                        <td class="col-phone-numbers">{{ lead.match.meta.normalization?.phoneNumbers?.join(' | ') || '' }}</td>
                        <td class="col-urls">{{ lead.match.meta.normalization?.urls?.join(' | ') || '' }}</td>
                      </tr>
                    </tbody>
                  </template>
                </table>
              </div>
            </div>

            <!-- Keine Übernahme möglich: Nur Treffer mit 'block' oder aktivem Countdown Timer -->
            <div class="container" v-if="noTakeoverLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Übernahme nicht möglich <span class="container-count">{{ noTakeoverLeads.length }}</span></h2>
              <p>Bestandskunden und reservierte Potentiale könnnen nicht übernommen werden.</p>
              <div v-if="isAtCapacity" class="form-errors" style="margin-bottom:1rem;">Kapazitätslimit erreicht: Bitte bearbeiten Sie zuerst bestehende Eigenrecherche-Neukunden, um wieder Kapazität zu erhalten.</div>
              <div class="table-responsive">
                <table class="sticky-table">
                  <thead>
                      <tr>
                        <th class="col-highlightBorder">Sales Manager</th>
                        <th class="col-select"></th>
                        <th class="col-name">Name / Firma</th>
                        <th class="col-info">Info</th>
                        <th class="col-address">Adresse</th>
                        <th class="col-phone-numbers">Telefon</th>
                        <th class="col-urls">Url</th>
                      </tr>
                  </thead>
                    <template v-for="(lead, index) in noTakeoverLeads
                      .sort((a, b) => {
                        // Countdown Timer Treffer nach oben
                        const aCountdown = a.match?.status?.dateUnassign && isDateUnassignActive(a.match?.status?.dateUnassign);
                        const bCountdown = b.match?.status?.dateUnassign && isDateUnassignActive(b.match?.status?.dateUnassign);
                        if (aCountdown && !bCountdown) return -1;
                        if (!aCountdown && bCountdown) return 1;
                        return (a.matching?.score || 0) - (b.matching?.score || 0);
                      })" :key="lead.id">
                      <!-- first row: the lead that was found by the google places search-->
                      <tr class="spacer"><td colspan="7"></td></tr>
                      <tbody :class="lead.convertToBusinessPartnerColor" :title="getMatchExplanationTitle(lead)" @mouseover="handleCompareLeadToBusinessPartners(lead)" @mouseleave="clearLeadHover">
                        <tr>
                          <td rowspan="2">
                            <userAvatar
                              :size="48"
                              :showSalesAgent="true"
                              :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback"
                              :idUser="lead.match?.status?.salesAgent?.idUser"
                              :statusOverride="(!lead.match?.status?.salesAgent || lead.match?.status?.salesAgent.firstName === 'Dummy') ? { name: 'Nicht zugewiesen', status: getStatusSubline(lead.match?.status) } : undefined"
                            >
                              <template #default>
                                <template v-if="lead.match?.status?.dateUnassign && isDateUnassignActive(lead.match?.status?.dateUnassign)">
                                  <span class="status-label status-label--yellow status-label--yellow-vertical">
                                    <span class="date status-label-yellow-date">
                                      Freigabe am {{ lead.match?.status?.dateUnassign ? new Date(lead.match?.status?.dateUnassign).toLocaleDateString('de-DE') : '' }}
                                    </span>
                                    <CountdownBadge :dateUnassign="lead.match?.status?.dateUnassign" />
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedManually">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Manuell blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.blockedAutomatically">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Automatisch blockiert
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.capacityBlocked">
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Kapazität erreicht
                                  </span>
                                </template>
                                <template v-else-if="hasActiveContracts(lead.match?.status)">
                                  <span class="status-label status-label--indigo">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Bestandskunde
                                  </span>
                                </template>
                                <template v-else-if="lead.match?.status?.salesAgent && lead.match?.status?.salesAgent.firstName !== 'Dummy'">
                                  <span v-if="isLockedAssignedStatus(lead.match?.status)" class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                  <span v-else class="status-label status-label--green">
                                    <img src="@/assets/imgs/icons/lock-open-solid-full.svg" alt="Lock Open" class="status-label__icon" />
                                    {{ getAssignedStatusLabel(lead.match?.status) }}
                                  </span>
                                </template>
                                <template v-else>
                                  <span class="status-label status-label--red">
                                    <img src="@/assets/imgs/icons/lock-solid.svg" alt="Lock" class="status-label__icon" />
                                    Nicht übernehmbar
                                  </span>
                                </template>
                              </template>
                            </userAvatar>
                            <div class="selection-quality-corner">
                              <span :class="['selection-quality-badge', 'is-' + getQualityBucket(lead)]">{{ getQualityBucketLabel(getQualityBucket(lead)) }}</span>
                              <button
                                type="button"
                                class="selection-quality-info-btn"
                                @click.stop="toggleQualityInfo(lead)"
                                :aria-expanded="isQualityInfoOpen(lead)"
                                aria-label="Begründung anzeigen"
                              >i</button>
                              <div v-if="isQualityInfoOpen(lead)" class="selection-quality-info-panel">
                                <div class="selection-quality-panel-head">
                                  <span class="selection-quality-panel-title">Score</span>
                                  <span :class="['selection-quality-panel-score', `is-${getQualityBucket(lead)}`]">{{ getLeadScoreDisplay(lead) }}</span>
                                </div>
                                <hr class="selection-quality-panel-separator" />
                                <div class="selection-quality-info-list">
                                  <div
                                    v-for="(row, rIndex) in getMatchReasonRows(lead)"
                                    :key="`${getLeadQualityKey(lead)}-reason-${rIndex}`"
                                    class="selection-quality-info-item"
                                  >
                                    <img :src="row.iconSrc" class="selection-quality-info-icon" alt="" aria-hidden="true" />
                                    <span class="selection-quality-info-text">{{ row.label }}</span>
                                    <span v-if="row.metric" :class="['selection-quality-metric-pill', `is-${row.tone}`]">{{ row.metric }}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="col-select">
                            <!-- <span class="highlightBorder"></span>                -->
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.google" @click="handleSelectLead(lead, 'google')" :disabled="isSelectionDisabledAtCapacity(lead, 'google') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-lead' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <!-- <td class="col-similarity-indicator" rowspan="2"><img  class="imgWrapper" :class="{'inactive' : !businessPartner.match.name }" src="@/assets/imgs/icons/shuffle-solid.svg"></td> -->
                          <td class="col-name">
                            <!-- <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations"><img src="@/assets/imgs/icons/star-border-solid.svg">{{lead.meta.googlePlaces?.ratings?.count}}</div>
                              <div class="googleEvaluations">⌀ {{lead.meta.googlePlaces?.ratings?.average}}</div>
                              <div class="googleEvaluations">Neueste {{lead.meta.googlePlaces?.ratings?.dateLastRating}}</div>
                            </div> -->
                            <a class="button"
                                  target="_blank" 
                                  :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)"
                              >
                              <div v-if="!lead.matching?.name || !lead.match || !isLeadHovered(lead)">
                                <!-- <div class="logoWrapper"><img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google Logo"> <p>{{lead.meta.googlePlaces.name}}</p></div> -->
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <p>{{lead.name}}</p></div>
                              </div>
                              <div v-else>
                                <div class="logoWrapper google">
                                  <img src="@/assets/imgs/logos/google/Google__G__logo.png" 
                                      :title="`Bewertungen: Anzahl ${lead.meta.googlePlaces?.ratings?.count}, Durchschnitt ${lead.meta.googlePlaces?.ratings?.average}, Neueste ${lead.meta.googlePlaces?.ratings?.dateLastRating}`"
                                  > 
                                  <div>
                                    <p v-for="(token, index) in lead.matching.name.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</p>
                                  </div>
                                </div>
                              </div>
                            </a>
                          </td>
                          <td class="col-info">
                            <div class="googleEvaluationsWrapper">
                              <div class="googleEvaluations">{{formatMonthYear(lead.meta.googlePlaces?.ratings?.dateLastRating)}}</div>
                              <div class="rating-wrapper">
                                <template v-for="(star, index) in renderStars(lead.meta.googlePlaces?.ratings?.average || 0)" :key="index" >
                                  <img class="yellow" v-if="star === 'yellow'" src="@/assets/imgs/icons/star-border-solid.svg" alt="yellow" />
                                  <img class="grey" v-else src="@/assets/imgs/icons/star-border-solid-black.svg" alt="black" />
                                </template>
                              </div>                              
                              <div class="googleEvaluations">({{lead.meta.googlePlaces?.ratings?.count}})</div>
                            </div>
                          </td>
                          <td class="col-address">
                            <div v-if="!lead.matching?.address|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.address?.split(',').splice(0,2).join(", ")}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.address.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-phone-numbers">
                            <div v-if="!lead.matching?.phoneNumbers|| !lead.match || !isLeadHovered(lead)">
                              {{lead.meta.googlePlaces.phoneNumbers?.join(' | ') || ''}}
                            </div>
                            <div v-else>
                              <span v-for="(token, index) in lead.matching.phoneNumbers.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}&nbsp;</span>
                            </div>  
                          </td>
                          <td class="col-urls">
                            <a v-if="lead.meta.googlePlaces.urls" class="button"
                                  target="_blank" 
                                  :href=lead.meta.googlePlaces.urls[0]
                              >
                              <div v-if="!lead.matching?.urls|| !lead.match || !isLeadHovered(lead)">
                                {{lead.meta.googlePlaces.urls?.join(' | ')|| ''}}
                              </div>
                              <div v-else>
                                <span v-for="(token, index) in lead.matching.urls.tokens" :key="index" :class="{'indicate-similarity': token.match}">{{token.token}}</span>
                              </div>  
                            </a>
                          </td>
                        </tr>
                        <tr v-if="lead.match" class="match">
                          <td class="col-select">          
                                    <input  v-if="lead.convertToBusinessPartner !== 'block'" 
                                      type="checkbox" v-model="lead.selection.businessPartner" @click="handleSelectLead(lead, 'businessPartner')" :disabled="isSelectionDisabledAtCapacity(lead, 'businessPartner') || isDateUnassignActive(lead.match?.status?.dateUnassign)"
                                      :class="[{'disabled' : (lead.convertToBusinessPartner !== 'allow-businessPartner' && lead.convertToBusinessPartner !== 'allow-both') || isDateUnassignActive(lead.match?.status?.dateUnassign)}]" 
                                    >
                          </td>
                          <td class="col-name">
                            <div class="logoWrapper">
                              <img src="@/assets/imgs/logos/sellwerk/sellwerk-favicon.svg" alt="Sellwerk Logo">
                                <span>{{lead.match.name}}</span>
                                <span class="gp-pill" :title="lead.match.confidence">{{ lead.match.meta.gpNo }}</span>
                            </div>
                          </td>
                          <td class="col-info">
                            <span v-if="lead.match?.status?.contracts && lead.match.status?.contracts !== '--- -- -- --'">
                              <div class="productsIcon-wrapper">
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('GS')) }"
                                    src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="Abo in GS" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('TB')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="Abo im TB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <img :class="{ 'active': (lead.match.status.contracts?.includes('OE')) }"
                                    src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="Abo im ÖTB" />
                                </div>
                                <div class="indicator-wrapper">
                                  <div :class="{ 'active': (lead.match.status.contracts?.includes('OMP')) }" class="omp">omp</div>
                                </div>
                              </div>
                            </span>
                          </td>
                          <td class="col-address">
                            <div class="address-distance-cell">
                              <span>{{lead.match.meta.normalization?.address?.split(',').splice(0,2).join(", ")}}</span>
                              <span :class="['distance-pill', getDistanceClass(lead.match.distance)]">{{ getDistanceLabel(lead.match.distance) }}</span>
                            </div>
                          </td>
                          <td class="col-phone-numbers">
                            <div>{{lead.match.meta.normalization?.phoneNumbers?.join(' | ') || ''}}</div>
                          </td>
                          <td class="col-urls">
                            <div>{{lead.match.meta.normalization?.urls?.join(' | ')|| ''}}</div>
                          </td>
                        </tr>
                        <tr v-else class="noMatch">
                          <td colspan="7">
                            kein matchender GP gefunden 
                          </td>
                        </tr>
                      </tbody>
                    </template>
                </table>
              </div>
            </div>

            <!-- Fehler beim Matching: eigene Liste -->
            <div class="container container--errors" v-if="errorLeads.length > 0" ref="businessPartnerListDesktop">
              <h2 class="container-headline">Fehler beim Abgleich <span class="container-count">{{ errorLeads.length }}</span></h2>
              <p>Treffer, bei denen beim Abgleich ein Fehler aufgetreten ist.</p>
              <div class="table-responsive">
                <table class="sticky-table">
                  <thead>
                    <tr>
                      <th class="col-highlightBorder">Sales Manager</th>
                      <th class="col-select"></th>
                      <th class="col-name">Name / Firma</th>
                      <th class="col-info">Info</th>
                      <th class="col-address">Adresse</th>
                      <th class="col-phone-numbers">Telefon</th>
                      <th class="col-urls">Url</th>
                    </tr>
                  </thead>
                  <template v-for="lead in errorLeads.sort((a,b) => (a.name || '').localeCompare(b.name || ''))" :key="lead.id">
                    <tr class="spacer"><td colspan="7"></td></tr>
                    <tbody class="google error-lead" :title="getMatchExplanationTitle(lead)">
                      <tr>
                        <td class="col-highlightBorder no-sales-manager-cell">
                          <div class="selection-quality-corner">
                            <span class="selection-quality-badge is-error">Fehler</span>
                            <button
                              type="button"
                              class="selection-quality-info-btn"
                              @click.stop="toggleQualityInfo(lead)"
                              :aria-expanded="isQualityInfoOpen(lead)"
                              aria-label="Fehlermeldung anzeigen"
                            >i</button>
                            <div v-if="isQualityInfoOpen(lead)" class="selection-quality-info-panel">
                              <div class="selection-quality-panel-head">
                                <span class="selection-quality-panel-title">Fehler beim Abgleich</span>
                              </div>
                              <hr class="selection-quality-panel-separator" />
                              <div class="selection-quality-info-list">
                                <div class="selection-quality-info-item">
                                  <img :src="iconInfo" class="selection-quality-info-icon" alt="" aria-hidden="true" />
                                  <span class="selection-quality-info-text">{{ lead.errorMessage || 'Keine Details verfügbar' }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <span class="no-sales-manager-placeholder">-</span>
                        </td>
                        <td class="col-select">
                          <input type="checkbox" disabled class="own-disabled-checkbox" />
                        </td>
                        <td class="col-name">
                          <a class="button" target="_blank" :href="'https://www.google.com/search?q=' + encodeURIComponent(lead.name + ' ' + lead.meta.googlePlaces.address)">
                            <div class="logoWrapper google">
                              <img src="@/assets/imgs/logos/google/Google__G__logo.png" />
                              <p>{{ lead.name }}</p>
                            </div>
                          </a>
                        </td>
                        <td class="col-info">Fehler beim Abgleich</td>
                        <td class="col-address">{{ lead.meta.googlePlaces.address?.split(',').splice(0,2).join(', ') }}</td>
                        <td class="col-phone-numbers">{{ lead.meta.googlePlaces.phoneNumbers?.join(' | ') || '' }}</td>
                        <td class="col-urls">
                          <a v-if="lead.meta.googlePlaces.urls" target="_blank" :href="lead.meta.googlePlaces.urls[0]">{{ lead.meta.googlePlaces.urls?.[0] }}</a>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </table>
              </div>
            </div>
          </div>
          <div v-if="userStore.currentSelection.leadGeneration.search.isLoading === true" class="container main-content cell-grid">
              <div class="loader" id="loader"></div>
          </div>
          <div class='wave-wrapper'>
            <div class='wave -one'></div>
            <div class='wave -two'></div>
            <div class='wave -three'></div>
          </div>
        </div>
        <!-- <div class="btn filter accent" @click="handleOpenFilter()">Filter setzen</div> -->
        <!-- <BusinessPartnerFilter v-if="userStore.selectedMedia === 'desktop'"></BusinessPartnerFilter> -->
      </div>
    </main>
    <reminder></reminder>
  </div>
</template>


<style lang="scss" scoped>
@use "sass:color";
@use "@/scss/settings/variables.scss" as variables;

// .col-name p,
// .col-name i,
// .col-name span {
//   text-overflow: ellipsis;
//   white-space: nowrap;
//   width: 300px;
//   overflow: hidden;
// }

.form-errors { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; text-align: center; padding:.5rem .6rem; border-radius:10px; border:1px solid #8b1f1f; background:#2a1717; color:#fecaca; font-size:.7rem; list-style:disc; margin-top: 1em; }
.theme--light .form-errors { background:#fff1f2; border-color:#fda4af; color:#7f1d1d; }
.form-errors li { margin-left:1rem; }

.container-headline .container-count {
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  padding: 0.15em 0.65em;
  border-radius: 999px;
  font-size: 0.72em;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.03em;
  vertical-align: middle;
  color: #64748b;
  background: transparent;
  border: 1.5px solid #cbd5e1;
  box-shadow: none;
  transition: background 0.15s, color 0.15s;
}

.theme--light .container-headline .container-count {
  color: #475569;
  border-color: #cbd5e1;
  background: transparent;
}

// Style für die hervorgehobene Pill und roten Button
.selected-pill {
  display: inline-block;
  background: variables.$color-sellwerk-grade-one;
  color: #fff;
  border-radius: 999px;
  padding: 0.1em 0.8em;
  font-weight: 700;
  margin-right: 0.3em;
  font-size: 1em;
  vertical-align: middle;
  letter-spacing: 0.02em;
}

.convertLeads.danger {
  background: #fff;
  color: variables.$color-sellwerk-grade-one;
  border: 1.5px solid variables.$color-sellwerk-grade-one;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: none;
  transition: background 0.18s, color 0.18s, border 0.18s;
}
.convertLeads.danger:hover:not(:disabled),
.convertLeads.danger:focus-visible:not(:disabled) {
  background: rgba(244, 67, 54, 0.08); // sehr dezentes Rot
  color: color.adjust(variables.$color-sellwerk-grade-one, $lightness: -10%);
  border-color: color.adjust(variables.$color-sellwerk-grade-one, $lightness: -10%);
}
.convertLeads.danger:active:not(:disabled) {
  background: rgba(244, 67, 54, 0.16);
  color: color.adjust(variables.$color-sellwerk-grade-one, $lightness: -15%);
  border-color: color.adjust(variables.$color-sellwerk-grade-one, $lightness: -15%);
}
.convertLeads.danger:disabled {
  background: #f3f4f6;
  color: #bbb;
  border-color: #eee;
}

.dropdown-pill--capacity.capacity-green,
.legend-text.capacity-green {
  background: variables.$color-sellwerk-grade-five !important;
  color: #fff !important;
}
.dropdown-pill--capacity.capacity-yellow,
.legend-text.capacity-yellow {
  background: variables.$color-sellwerk-grade-three !important;
  color: #222 !important;
}
.dropdown-pill--capacity.capacity-red,
.legend-text.capacity-red {
  background: variables.$color-sellwerk-grade-one !important;
  color: #fff !important;
}

    .modern-loading-preview {
      margin: 1.2em 0 1.5em 0;
      padding: 1.1em 1.2em 1.2em 1.2em;
      background: #f8fafc;
      border-radius: 1.1em;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      gap: 0.7em;
      align-items: stretch;
      width: 100%;
      border: 1px solid #e0e6ef;
    }
  
    .modern-loading-preview.light-theme {
      background: #f8fafc;
      color: #232a35;
    }
  
    .modern-loading-header {
      display: flex;
      gap: 0.7em;
      align-items: center;
      flex-wrap: wrap;
      font-size: 1.08em;
      margin-bottom: 0.2em;
      margin-top: 1em;
    }
  
    .modern-loading-bar-outer {
      width: 100%;
      height: 14px;
      background: #e0e6ef;
      border-radius: 8px;
      overflow: hidden;
      margin: 0.2em 0 1em 0;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      border: 1px solid #e0e6ef;
    }
  
    .modern-loading-bar-inner {
      height: 100%;
      background: linear-gradient(90deg, #ffb347 0%, #ffe082 100%);
      border-radius: 8px 0 0 8px;
      transition: width 0.4s cubic-bezier(.4, 1.4, .6, 1);
    }
  
    .modern-loading-percent {
      font-weight: 700;
      color: #ff8800;
      margin-left: auto;
      font-size: 1.08em;
      letter-spacing: 0.02em;
    }
  
    .modern-loading-numbers {
      display: flex;
      justify-content: space-between;
      font-size: 0.98em;
      color: #b0b8c9;
      margin-top: 0.1em;
      gap: 1em;
    }
  
    .modern-loading-label {
      color: #ff8800;
      font-weight: 600;
      margin-left: 1.2em;
    }
  
    .status-label--yellow-vertical {
      flex-direction: column;
      align-items: center;
    }
  
    .status-label-yellow-date {
      font-weight: black;
      font-size: 0.97em;
      margin-bottom: 2px;
    }
  
    .sm-wrapper:not(:has(.rankIcon)) {
      gap: 0;
    }

    // .sm-name {
    //   margin-left: 0.75em;
    // }
  
    .status-labels-column {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 6px;
      margin-top: 2px;
      background: none;
      border-radius: 0;
      padding: 0;
      /* width: 180px;
      min-width: 180px;
      max-width: 220px; */
    }
  
    .status-label--fullwidth {
      width: 100%;
      min-width: 100%;
      justify-content: flex-start;
      box-sizing: border-box;
    }
  
    .status-label {
      display: flex;
      align-items: center;
      gap: 6px;
      border-radius: 8px;
      padding: 0.25em 0.85em .25em 0.75em;
      font-size: 0.95em;
      font-weight: bold;
      margin-right: 2px;
      margin-top: 6px;
      // margin-left: -6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      letter-spacing: 0.03em;
      max-width: max-content;
      width: max-content;
    }
  
    .status-label.blocked {
      flex-direction: column;
    }
  
    .status-label__icon {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }
  
    .status-label--green {
      background: linear-gradient(90deg, #4caf50 0%, #81c784 100%);
      color: #fff !important;
    }
  
    .status-label--red {
      background: linear-gradient(90deg, #f44336 0%, #e57373 100%);
      color: #fff !important;
    }
  
    .status-label--yellow {
      background: linear-gradient(90deg, #ff9800 0%, #ffc107 100%);
      color: #222;
    }
  
    .status-label--blue {
      background: linear-gradient(90deg, #2196f3 0%, #64b5f6 100%);
      color: #fff;
    }
  
    /* Moderner Pillen-Style für Countdown-Badge */
    .countdown-badge-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      background: none;
      border-radius: 0;
      padding: 0;
      margin: 0.2em 0;
    }
  
    /* Dezente Farben für Status-Labels */
    .status-label--green {
      background: linear-gradient(90deg, #eafaf1 0%, #d6f5e3 100%);
      color: #256d3a !important;
    }
  
    .status-label--red {
      background: linear-gradient(90deg, #faeaea 0%, #f5d6d6 100%);
      color: #a13a3a !important;
    }
  
    .status-label--yellow {
      background: linear-gradient(90deg, #fff7e0 0%, #ffe9b3 100%);
      color: #7a5a00;
    }
  
    .status-label--blue {
      background: linear-gradient(90deg, #e3f0fb 0%, #cbe3fa 100%);
      color: #22577a;
    }
  
    .status-label--indigo {
      background: rgba(variables.$sellwerk-color-secondary, 0.2);
      color: variables.$sellwerk-color-main;
      box-shadow: none;
    }
  
    .query-dropdown-weighted {
      margin: 2rem 0 2rem 0;
      padding: 1.2rem 1.5rem;
      background: var(--sellwerk-bg-widget, #232a35);
      border-radius: 1.2rem;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    }
  
    /* Modernes, dezentes Matching-Tabellen-Design: Light Theme */
    .matching-table-modern {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0 0.2rem;
      background: #fff;
      border-radius: 1.2rem;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      font-size: 1.08rem;
    }
  
    .matching-table-modern th,
    .matching-table-modern td {
      padding: 0.6em 0.7em;
      text-align: left;
      background: transparent;
      border: none;
    }
  
    .matching-table-modern th {
      color: #ff8800;
      font-weight: 700;
      background: #f7f7f7;
      border-bottom: 1px solid #e0e0e0;
      font-size: 1.1rem;
    }
  
    .matching-row {
      transition: background 0.18s;
      background: #fff;
      border-radius: 0.7rem;
    }
  
    .matching-row-bp {
      background: #f7f7f7;
      color: #888;
      font-size: 0.98em;
      border-bottom: 1px solid #e0e0e0;
    }
  
    .matching-row.is-hovered,
    .matching-row-bp.is-hovered {
      background: #ffe7c2;
    }
  
    .match-token {
      display: inline-block;
      margin-right: 0.18em;
      padding: 0.13em 0.32em;
      border-radius: 0.3em;
      background: transparent;
      color: #444;
      transition: background 0.18s, color 0.18s;
      font-variant-numeric: tabular-nums;
      font-size: 1em;
    }
  
    .match-token.highlight {
      background: #ff8800;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 1px 4px rgba(255, 136, 0, 0.08);
    }
  
    .score-cell {
      text-align: center;
      vertical-align: middle;
    }
  
    .score-badge {
      display: inline-block;
      min-width: 2.5em;
      padding: 0.18em 0.7em;
      border-radius: 1em;
      font-weight: 700;
      font-size: 1.08em;
      background: #f7f7f7;
      border: 1.5px solid #e0e0e0;
      transition: background 0.18s, color 0.18s, border 0.18s;
    }
  
    .score-badge.score-good {
      background: #27ae60;
      color: #fff;
      border-color: #27ae60;
    }
  
    .score-badge.score-medium {
      background: #f39c12;
      color: #fff;
      border-color: #f39c12;
    }
  
    .score-badge.score-bad {
      background: #b00;
      color: #fff;
      border-color: #b00;
    }
  
    @media (max-width: 900px) {
  
      .matching-table-modern th,
      .matching-table-modern td {
        font-size: 0.98em;
        padding: 0.5em 0.3em;
      }
  
      .score-badge {
        font-size: 0.98em;
        padding: 0.13em 0.5em;
      }
    }
  
    @media (max-width: 600px) {
  
      .matching-table-modern th,
      .matching-table-modern td {
        font-size: 0.93em;
        padding: 0.4em 0.1em;
      }
  
      .score-badge {
        font-size: 0.93em;
        padding: 0.1em 0.3em;
      }
    }
  
    .dropdown-header {
      display: flex;
      align-items: center;
      gap: 1.2rem;
      font-size: 1.2rem;
      font-weight: 600;
    }
  
    .dropdown-label {
      font-size: 1.2rem;
      font-weight: 600;
      color: var(--sellwerk-color-main-accent, #ff8800);
    }
  
    .dropdown-count {
      font-size: 1rem;
      color: #a0aec0;
    }
  
    .dropdown-list {
      display: flex;
      /* flex-direction: column; */
      align-items: flex-start;
      gap: 2rem;
      width: 100%;
    }
  
    .dropdown-select {
      font-size: 1.1rem;
      padding: 0.6rem 1.2rem;
      border-radius: 0.7rem;
      border: 1px solid #475569;
      background: #232a35;
      color: #fff;
      min-width: 220px;
    }
  
    .dropdown-info {
      // margin-left: 2rem;
      min-width: 220px;
      display: flex;
      flex-direction: column;
      gap: 0.7rem;
    }
  
    .dropdown-details {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
  
    .dropdown-title {
      font-size: 1.15rem;
      font-weight: 600;
      color: var(--sellwerk-color-main-accent, #ff8800);
    }
  
    .dropdown-meta {
      display: flex;
      gap: 0.7rem;
      flex-wrap: wrap;
    }
  
    .dropdown-pill {
      background: #2d3944;
      color: #fff;
      border-radius: 999px;
      padding: 0.25em 0.9em;
      font-size: 1rem;
      font-weight: 500;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
    }
  
    .dropdown-pill--loading {
      background: #475569;
      color: #ff8800;
    }
  
    .dropdown-pill--potential {
      background: #ff8800;
      color: #232a35;
    }
  
    /* .buttons {
    display: flex;
    justify-content: space-between; 
  }
  
  table {
    width: 100%;
    margin: 0 auto;
    table-layout:fixed;
    border-collapse: collapse;
  }
  
  
  tr.match {
    vertical-align: top;
  }
  
  .imgWrapper {
    display: none;
  }
  
  td{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: text-top;
  }
  
  .col-similarity-indicator {
    width: 1%;
    display: none;
    font-size: revert;
  }
  
  .col-select {
    width: 2%;
  }
  
  .col-name,
  .col-address,
  .col-urls{
    width: 28%;
  }
  
  .col-phone-numbers {
    width: 10%;
  } */
  
  
  
    /* Sticky Table Styles */
    .table-responsive {
      overflow-x: auto;
      /* Kein overflow-y, damit sticky funktioniert */
    }
  
    .sticky-table th {
      position: sticky;
      top: 0;
      background: #fff;
      z-index: 2;
      /* Optional: Schatten für bessere Sichtbarkeit */
      // box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
    }
</style>

<style scoped>
.convertLeads.accent-orange {
  background: #fff7e0;
  color: #d97706;
  border: 1.5px solid #ffe6c2;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: none;
  transition: background 0.18s, color 0.18s, border 0.18s;
}

.convertLeads.accent-orange:hover:not(:disabled),
.convertLeads.accent-orange:focus-visible:not(:disabled) {
  background: #ffe6c2;
  color: #b45309;
  border-color: #ffd699;
}

.convertLeads.accent-orange:active:not(:disabled) {
  background: #ffe6c2;
  color: #a16207;
  border-color: #ffd699;
}

.convertLeads.accent-orange:disabled {
  background: #f3f4f6;
  color: #bbb;
  border-color: #eee;
}

.convertLeads.accent-orange-outline {
  background: transparent;
  color: #d97706;
  border: 1.5px solid #ffe6c2;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: none;
  transition: background 0.18s, color 0.18s, border 0.18s;
}

.convertLeads.accent-orange-outline:hover:not(:disabled),
.convertLeads.accent-orange-outline:focus-visible:not(:disabled) {
  background: #fff7e0;
  color: #b45309;
  border-color: #ffd699;
}

.convertLeads.accent-orange-outline:active:not(:disabled) {
  background: #ffe6c2;
  color: #a16207;
  border-color: #ffd699;
}

.convertLeads.accent-orange-outline:disabled {
  background: #f3f4f6;
  color: #bbb;
  border-color: #eee;
}

/* Ladebalken für Dropdown Info */
.dropdown-loading-bar {
  margin-top: 0.7rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.loading-bar {
  width: 120px;
  height: 8px;
  background: #475569;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.loading-bar-progress {
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, #ff8800 0%, #ffd580 100%);
  border-radius: 4px;
  animation: loadingBarAnim 1.2s infinite ease-in-out;
}

@keyframes loadingBarAnim {
  0% {
    width: 10%;
    opacity: 0.7;
  }

  50% {
    width: 90%;
    opacity: 1;
  }

  100% {
    width: 10%;
    opacity: 0.7;
  }
}

.dropdown-loading-label {
  font-size: 1rem;
  color: #ff8800;
  font-weight: 500;
}

.theme--light .loading-bar {
  background: #d5dde5;
}

.theme--light .loading-bar-progress {
  background: linear-gradient(90deg, #ff8800 0%, #ffd580 100%);
}

.theme--light .dropdown-loading-label {
  color: #ff8800;
}

/* Light theme overrides */
.theme--light .query-dropdown-weighted {
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.theme--light .dropdown-label {
  color: #ff8800;
}

.theme--light .dropdown-count {
  color: #64748b;
}

.theme--light .dropdown-select {
  background: #fff;
  color: #232a35;
  border: 1px solid #d5dde5;
}

.theme--light .dropdown-info {
  color: #232a35;
}

.theme--light .dropdown-title {
  color: #ff8800;
}

.theme--light .dropdown-meta {
  color: #232a35;
}

.theme--light .dropdown-pill {
  background: #f3f4f6;
  color: #232a35;
  border: 1px solid #d5dde5;
}

.theme--light .dropdown-pill--loading {
  background: #ffe6c2;
  color: #ff8800;
}

.theme--light .dropdown-pill--potential {
  background: #ff8800;
  color: #fff;
}

.theme--light .quality-chip {
  background: #ffffff;
  border-color: #d5dde5;
  color: #475569;
}

.theme--light .quality-chip.active {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.theme--light .quality-chip.is-high.active {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.theme--light .quality-chip.is-medium.active {
  background: #fef9c3;
  border-color: #eab308;
  color: #713f12;
}

.theme--light .quality-chip.is-low.active {
  background: #fff7ed;
  border-color: #f97316;
  color: #7c2d12;
}

.theme--light .quality-chip.is-none.active {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.matching-quality-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0 0 1rem 0;
}

.quality-toolbar-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  margin-right: 0.25rem;
}

.quality-chip {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  padding: 0.25rem 0.7rem;
}

.quality-chip.active {
  background: #22c55e;
  border-color: #16a34a;
  color: #fff;
}

.quality-chip.is-high.active {
  background: #22c55e;
  border-color: #16a34a;
  color: #fff;
}

.quality-chip.is-medium.active {
  background: #eab308;
  border-color: #ca8a04;
  color: #1a1a1a;
}

.quality-chip.is-low.active {
  background: #f97316;
  border-color: #ea580c;
  color: #fff;
}

.quality-chip.is-none.active {
  background: #22c55e;
  border-color: #16a34a;
  color: #fff;
}

.quality-chip.is-error {
  border-color: #f87171;
  color: #7f1d1d;
}

.quality-chip.is-error.active {
  background: #ef4444;
  border-color: #dc2626;
  color: #fff;
}

.selection-quality-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  border-radius: 999px;
  padding: 0.15rem 0.35rem;
  border: 1px solid transparent;
  white-space: nowrap;
}

.selection-quality-corner {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.2rem;
}

.container {
  position: relative;
  z-index: 0;
}

.container:has(.selection-quality-info-panel) {
  z-index: 40;
}

td[rowspan='2'] {
  position: relative;
}

td[rowspan='2']:has(.selection-quality-info-panel) {
  z-index: 1200;
}

td[rowspan='2'] .selection-quality-corner {
  position: absolute;
  top: 0.2rem;
  left: 0.2rem;
  margin: 0;
}

.selection-quality-info-btn {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.selection-quality-info-btn[aria-expanded='true'] {
  background: #ffedd5;
  border-color: #fdba74;
  color: #9a3412;
}

.selection-quality-info-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1201;
  min-width: 220px;
  max-width: 360px;
  width: max-content;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  padding: 0.4rem 0.5rem;
  z-index: 999
}

.selection-quality-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.selection-quality-panel-title {
  font-size: 0.66rem;
  font-weight: 700;
  color: #334155;
}

.selection-quality-panel-score {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.12rem 0.45rem;
  font-size: 0.63rem;
  font-weight: 800;
  border: 1px solid transparent;
}

.selection-quality-panel-score.is-high {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
}

.selection-quality-panel-score.is-medium {
  background: #ffedd5;
  border-color: #fdba74;
  color: #9a3412;
}

.selection-quality-panel-score.is-low,
.selection-quality-panel-score.is-none {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
}

.selection-quality-panel-separator {
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 0.35rem 0 0.4rem;
}

.selection-quality-info-list {
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
}

.selection-quality-info-item {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 0.32rem;
}

.selection-quality-info-icon {
  width: 12px;
  height: 12px;
  opacity: 0.72;
  filter: grayscale(0.15);
}

.selection-quality-info-text {
  font-size: 0.65rem;
  color: #475569;
  line-height: 1.25;
}

.selection-quality-metric-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.12rem 0.42rem;
  font-size: 0.62rem;
  font-weight: 700;
  border: 1px solid transparent;
  white-space: nowrap;
}

.selection-quality-metric-pill.is-good {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
}

.selection-quality-metric-pill.is-info {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.selection-quality-metric-pill.is-warn {
  background: #ffedd5;
  border-color: #fdba74;
  color: #9a3412;
}

.selection-quality-metric-pill.is-danger {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
}

.selection-quality-metric-pill.is-indigo {
  background: rgba(151, 210, 212, 0.22);
  border-color: rgba(40, 129, 126, 0.34);
  color: var(--sellwerk-color-main);
}

.own-lead-bp-link {
  color: var(--sellwerk-color-main-accent, #ff8800);
  text-decoration: underline;
  font-weight: 600;
  &:hover { opacity: 0.8; }
}

.no-sales-manager-cell {
  vertical-align: top;
}

.no-sales-manager-placeholder {
  display: none;
}

.own-disabled-checkbox {
  opacity: 0.45;
  cursor: not-allowed;
}

/* GP-Nummer Pill */
.gp-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.1em 0.55em;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  white-space: nowrap;
  font-family: monospace;
}

/* Distanz-Pill Container */
.address-distance-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.address-distance-cell > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Distanz Pills */
.distance-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.1em 0.55em;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  width: fit-content;
  flex-shrink: 0;
}

.distance-pill.dist-green {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.distance-pill.dist-orange-light {
  background: #fef9c3;
  color: #854d0e;
  border: 1px solid #fde047;
}

.distance-pill.dist-orange {
  background: #ffedd5;
  color: #9a3412;
  border: 1px solid #fdba74;
}

.distance-pill.dist-red {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.distance-pill.dist-neutral {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.selection-quality-badge.is-high {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.selection-quality-badge.is-medium {
  background: #fef9c3;
  border-color: #eab308;
  color: #713f12;
}

.selection-quality-badge.is-low {
  background: #fff7ed;
  border-color: #f97316;
  color: #7c2d12;
}

.selection-quality-badge.is-none {
  background: #dcfce7;
  border-color: #22c55e;
  color: #14532d;
}

.selection-quality-badge.is-error {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
}

.error-info-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.error-info-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
}

.rating-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.rating-wrapper img {
  width: 14px;
  height: 14px;
  object-fit: contain;
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.95));
}

.rating-wrapper img.grey {
  opacity: 0.35;
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.95)) grayscale(1);
}

.sticky-table th.col-highlightBorder,
.sticky-table td.col-highlightBorder,
.sticky-table tbody > tr > td:first-child[rowspan='2'] {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  box-sizing: border-box;
}

.sticky-table {
  width: 100%;
  table-layout: fixed;
}

.sticky-table th.col-select,
.sticky-table td.col-select {
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  padding: 0;
}

.sticky-table th.col-name,
.sticky-table td.col-name {
  width: 320px;
  min-width: 320px;
}

.sticky-table th.col-info,
.sticky-table td.col-info {
  width: 170px;
  min-width: 170px;
}

.sticky-table th.col-address,
.sticky-table td.col-address {
  width: 280px;
  min-width: 280px;
}

.sticky-table th.col-phone-numbers,
.sticky-table td.col-phone-numbers {
  width: 140px;
  min-width: 140px;
}

.sticky-table th.col-urls,
.sticky-table td.col-urls {
  width: 180px;
  min-width: 180px;
}

.query-dropdown-wrapper {
  margin: 1rem 0 2rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.fullscreen-spinner-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.list-loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.72);
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.spinner-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.takeover-loading-card {
  width: min(680px, calc(100vw - 2rem));
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.55);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.2);
  padding: 1.5rem 1.35rem;
  gap: 0.65rem;
  text-align: center;
}

.loadingDescription--main {
  margin-top: 0.9rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.loadingDescription--sub {
  margin-top: 0.1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
}

.takeover-loading-info {
  margin-top: 0.55rem;
  max-width: 580px;
  font-size: 0.9rem;
  line-height: 1.45;
  color: #475569;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.conversion-feedback-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(8, 15, 28, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.conversion-feedback-modal {
  width: min(920px, 100%);
  max-height: min(88vh, 900px);
  overflow: auto;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.26);
  background: #111c33;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
  padding: 1.35rem;
  color: #e2e8f0;
  animation: conversionModalIn 0.26s ease-out;
}

.theme--light .conversion-feedback-modal {
  background: #ffffff;
  color: #0f172a;
}

.conversion-feedback-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.conversion-feedback-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
}

.conversion-feedback-success-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #2FAC6A;
  color: #fff;
  font-weight: 800;
  margin-top: 0.1rem;
  box-shadow: 0 10px 22px rgba(34, 197, 94, 0.35);
}

.conversion-feedback-eyebrow {
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.72rem;
  color: #f59e0b;
}

.conversion-feedback-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.conversion-feedback-subline {
  margin: 0.35rem 0 0;
  color: #94a3b8;
  font-size: 0.85rem;
}

.conversion-feedback-close,
.conversion-feedback-primary,
.conversion-feedback-secondary,
.conversion-feedback-item-button {
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1.15;
}

.conversion-feedback-close {
  padding: 0.48rem 0.78rem;
  background: rgba(148, 163, 184, 0.1);
  border-color: rgba(148, 163, 184, 0.2);
  color: inherit;
}

.conversion-feedback-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
  margin-top: 1rem;
}

.conversion-feedback-stat {
  border-radius: 18px;
  padding: 0.95rem 1rem;
  background: rgba(151, 210, 212, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.conversion-feedback-stat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(151, 210, 212, 0.55);
  background: rgba(151, 210, 212, 0.22);
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0.14rem 0.54rem;
  margin-bottom: 0.42rem;
  color: #97d2d4;
}

.conversion-feedback-stat--wide {
  grid-column: span 3;
}

.conversion-feedback-stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
}

.conversion-feedback-stat-value--small {
  font-size: 1rem;
}

.conversion-feedback-stat-label {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.78rem;
  color: #94a3b8;
}

.theme--light .conversion-feedback-stat {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.theme--light .conversion-feedback-stat-label {
  color: #64748b;
}

.conversion-feedback-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.1rem;
}

.conversion-feedback-primary {
  padding: 0.56rem 0.88rem;
  background: #ef7d00;
  color: #fff;
  border-color: rgba(239, 125, 0, 0.65);
  box-shadow: none;
}

.conversion-feedback-secondary {
  padding: 0.56rem 0.88rem;
  background: rgba(40, 129, 126, 0.12);
  border-color: rgba(40, 129, 126, 0.32);
  color: inherit;
}

.conversion-feedback-section {
  margin-top: 1.25rem;
}

.conversion-feedback-section h3 {
  margin: 0 0 0.75rem;
  font-size: 1rem;
}

.conversion-feedback-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.conversion-feedback-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 0.72rem 0.82rem;
  border-radius: 18px;
  background: rgba(148, 163, 184, 0.07);
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.conversion-feedback-item-leading {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #28817e;
  box-shadow: 0 0 0 4px rgba(40, 129, 126, 0.22);
}

.conversion-feedback-item-content {
  min-width: 0;
}

.theme--light .conversion-feedback-item {
  background: #ffffff;
  border-color: #e2e8f0;
}

.conversion-feedback-item-title {
  font-weight: 700;
}

.conversion-feedback-item-meta {
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: #94a3b8;
}

.conversion-feedback-opened-pill {
  margin-left: 0.45rem;
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  border: 1px solid rgba(40, 129, 126, 0.35);
  background: rgba(40, 129, 126, 0.12);
  color: #28817e;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.theme--light .conversion-feedback-item-meta {
  color: #64748b;
}

.conversion-feedback-item-button {
  padding: 0.5rem 0.8rem;
  background: rgba(40, 129, 126, 0.16);
  border-color: rgba(40, 129, 126, 0.36);
  color: #28817e;
  text-decoration: none;
}

.conversion-feedback-errors {
  margin: 0;
  padding: 0.3rem 0;
  border-radius: 0;
  border: none;
  background: transparent;
  color: #fecaca;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.conversion-feedback-errors li {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: rgba(252, 165, 165, 0.08);
  border: 1px solid rgba(252, 165, 165, 0.18);
  font-size: 0.82rem;
  line-height: 1.45;
}

.conversion-feedback-errors li::before {
  content: '!';
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(252, 165, 165, 0.28);
  border: 1px solid rgba(252, 165, 165, 0.5);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 800;
  color: #fca5a5;
  margin-top: 0.05rem;
}

.theme--light .conversion-feedback-errors {
  color: #991b1b;
  background: transparent;
  border: none;
}

.theme--light .conversion-feedback-errors li {
  background: #fff5f5;
  border-color: #fecaca;
  color: #991b1b;
}

.theme--light .conversion-feedback-errors li::before {
  background: rgba(220, 38, 38, 0.12);
  border-color: #fca5a5;
  color: #dc2626;
}

.theme--light .conversion-feedback-secondary {
  border-color: rgba(40, 129, 126, 0.28);
  background: rgba(40, 129, 126, 0.08);
}

.theme--light .conversion-feedback-item-button {
  color: #28817e;
  border-color: rgba(40, 129, 126, 0.3);
}

@keyframes conversionModalIn {
  from {
    transform: translateY(12px) scale(0.99);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@media (max-width: 720px) {
  .conversion-feedback-summary {
    grid-template-columns: 1fr;
  }

  .conversion-feedback-stat--wide {
    grid-column: span 1;
  }

  .conversion-feedback-item {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .conversion-feedback-title-wrap {
    align-items: center;
  }

  .conversion-feedback-success-icon {
    width: 30px;
    height: 30px;
  }

  .conversion-feedback-item-leading {
    display: none;
  }

  .conversion-feedback-item-button {
    width: 100%;
    text-align: center;
  }
}
</style>